/* eslint-disable no-undef, react/no-unescaped-entities */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatET } from '../utils/timeUtils';
import { getApiErrorMessage } from '../lib/errorUtils';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import {
  Users, UserPlus, Edit, Trash2, Loader2, Mail, MailCheck, Shield,
  ChevronDown, ChevronRight, Plus, GraduationCap, FileText, BarChart3,
  DollarSign, Lock, Activity, ClipboardList, BookOpen, CheckCircle, Clock,
  Phone, Building2, Eye, Power, Upload, ArrowRight, Send, Award,
  ToggleLeft, ToggleRight, LogIn, PhoneCall, StickyNote, Briefcase,
  FolderOpen, ExternalLink, Gift, Settings, Globe, Plane, Ship, Ticket, Car, Search,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
// v3.18-1321 — Employee duplicate resolution + operational assignments.
import { EmployeeDuplicateDialog } from '../components/EmployeeDuplicateDialog';
import EmployeeAssignmentsPanel from '../components/EmployeeAssignmentsPanel';
// v3.18-1325B — Designated Operational VO Staff.
import OperationalRolesPanel from '../components/OperationalRolesPanel';

const INTERNAL_ROLES = [
  { value: 'ptc_admin', label: 'PTC Admin' },
  { value: 'ptc_director', label: 'PTC Director' },
  { value: 'ptc_manager', label: 'PTC Manager' },
  { value: 'ptc_team_member', label: 'PTC Team Member' },
  { value: 'ptc_sales_rep', label: 'PTC Sales Rep' },
];
const EXTERNAL_ROLES = [
  // v3.21-1631 · Canonical external partner role hierarchy.
  { value: 'company_principal',   label: 'Principal' },        // owner-level
  { value: 'company_director',    label: 'Director 1' },       // senior operational
  { value: 'manager_director',    label: 'Manager Director' }, // team leader
  { value: 'company_team_member', label: 'Team Member' },
  { value: 'company_sales_rep',   label: 'Sales Rep' },
];
// v3.15-959: VO Officer role — only for external timeshare_exit companies.
const VO_OFFICER_ROLE = { value: 'vo_officer', label: 'VO Officer' };
const ALL_ROLES = [...INTERNAL_ROLES, ...EXTERNAL_ROLES, VO_OFFICER_ROLE];

const roleColors = {
  ptc_admin: 'bg-purple-100 text-purple-800',
  ptc_director: 'bg-indigo-100 text-indigo-800',
  ptc_manager: 'bg-blue-100 text-blue-800',
  ptc_team_member: 'bg-slate-100 text-slate-800',
  ptc_principal_1: 'bg-amber-100 text-amber-900',
  ptc_sales_rep: 'bg-emerald-100 text-emerald-800',
  // v3.21-1631 · External partner hierarchy colors.
  company_principal:   'bg-rose-100 text-rose-800',    // owner
  company_director:    'bg-amber-100 text-amber-800',  // Director 1
  manager_director:    'bg-orange-100 text-orange-800',
  // Legacy aliases still shown until migration runs.
  company_admin:       'bg-rose-100 text-rose-800',
  company_manager:     'bg-orange-100 text-orange-800',
  company_team_member: 'bg-slate-100 text-slate-800',
  company_sales_rep: 'bg-teal-100 text-teal-800',
  // v3.15-959: VO Officer
  vo_officer: 'bg-rose-100 text-rose-800',
};

const formatRole = (role) => {
  if (!role) return '';
  // v3.15-959: keep "VO" capitalized.
  if (role === 'vo_officer') return 'VO Officer';
  return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const ONBOARDING_STAGES = ['Applied', 'Interview', 'Approved', 'Docs', 'Training', 'Active'];
const DEFAULT_DOCUMENTS = [
  { name: 'NDA', type: 'nda' },
  { name: 'Contractor Agreement', type: 'contractor_agreement' },
  { name: 'Pay Plan', type: 'pay_plan' },
  { name: 'W9', type: 'w9' },
];

// =================== TEAM MEMBERS TAB ===================
const TeamMembersTab = ({ users, companies, currentUser, isAdmin, onRefresh, allUsers }) => {
  const navigate = useNavigate();
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ full_name: '', email: '', phone: '', password: '', role: 'company_team_member', company_id: '', extension: '', position: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkUsernames, setBulkUsernames] = useState('');
  const [bulkPassword, setBulkPassword] = useState('ChangeMe123!');
  const [bulkRole, setBulkRole] = useState('company_team_member');
  const [bulkCompanyId, setBulkCompanyId] = useState('');
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  // v3.15-908: Send/Resend Credentials — bulk + per-row.
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [sendingUserIds, setSendingUserIds] = useState(() => new Set());
  const [sendingBulkCreds, setSendingBulkCreds] = useState(false);
  // v3.18-1321 — Duplicate resolution + assignments panel state.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);      // { email, targetCompanyId, targetCompanyName, incoming }
  const [showAssignmentsForUser, setShowAssignmentsForUser] = useState(false);
  // v3.21-1634 · Block 2 · Defect B — Extension + Voicemail Greeting are
  // PTC-internal telephony fields.  External partner admins (Principal /
  // Director 1 / Manager Director) MUST never see or edit them, even
  // though they can otherwise manage their own company's team members.
  const isInternalCaller = ((currentUser?.role || '').startsWith('ptc_'));
  const toggleSelectUser = (id) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const sendCredentialsForUsers = async (ids, scope = 'single') => {
    if (!ids || ids.length === 0) return;
    if (scope === 'bulk') setSendingBulkCreds(true);
    setSendingUserIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
    try {
      const res = await api.post('/users/send-credentials', { userIds: ids, confirm: true });
      const sent = res.data?.summary?.sent ?? 0;
      const failed = res.data?.summary?.failed ?? 0;
      if (sent > 0) toast.success(`${sent} email${sent === 1 ? '' : 's'} sent successfully`);
      if (failed > 0) {
        const reason = (res.data?.failed?.[0]?.error) || 'unknown reason';
        toast.error(`${failed} failed (${reason})`);
      }
      onRefresh?.();
      if (scope === 'bulk') setSelectedUserIds(new Set());
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to send credentials'));
    } finally {
      setSendingUserIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      if (scope === 'bulk') setSendingBulkCreds(false);
    }
  };
  const isExternalDirector = currentUser?.role === 'company_director';

  // v3.15-671: Get email domain for selected company
  const selectedCompany = useMemo(() => companies.find(c => c.id === newUser.company_id), [companies, newUser.company_id]);
  const emailDomain = selectedCompany?.email_domain || '';

  const selectedCompanyType = useMemo(() => companies.find(c => c.id === newUser.company_id)?.company_type || 'external', [companies, newUser.company_id]);
  // v3.15-959: industry_category drives VO Officer visibility.
  const selectedIndustryCategory = useMemo(() => companies.find(c => c.id === newUser.company_id)?.industry_category || '', [companies, newUser.company_id]);
  const availableRoles = useMemo(() => {
    if (selectedCompanyType === 'internal') return INTERNAL_ROLES;
    // v3.15-962: FAIL-OPEN — include VO Officer when TS or unknown industry.
    const ic = (selectedIndustryCategory || '').toLowerCase();
    const knownNonTs = ['solar_sales', 'solar_exit', 'travel_club_sales', 'automotive'];
    if (ic === 'timeshare_exit' || !ic) return [...EXTERNAL_ROLES, VO_OFFICER_ROLE];
    if (knownNonTs.includes(ic)) return EXTERNAL_ROLES;
    return [...EXTERNAL_ROLES, VO_OFFICER_ROLE];
  }, [selectedCompanyType, selectedIndustryCategory]);
  const editingCompanyType = useMemo(() => editingUser ? (companies.find(c => c.id === editingUser.company_id)?.company_type || 'external') : 'external', [companies, editingUser]);
  const editingIndustryCategory = useMemo(() => editingUser ? (companies.find(c => c.id === editingUser.company_id)?.industry_category || '') : '', [companies, editingUser]);
  const editingAvailableRoles = useMemo(() => {
    if (editingCompanyType === 'internal') return INTERNAL_ROLES;
    // v3.15-962: FAIL-OPEN — include VO Officer when TS or unknown industry.
    const ic = (editingIndustryCategory || '').toLowerCase();
    const knownNonTs = ['solar_sales', 'solar_exit', 'travel_club_sales', 'automotive'];
    if (ic === 'timeshare_exit' || !ic) return [...EXTERNAL_ROLES, VO_OFFICER_ROLE];
    if (knownNonTs.includes(ic)) return EXTERNAL_ROLES;
    return [...EXTERNAL_ROLES, VO_OFFICER_ROLE];
  }, [editingCompanyType, editingIndustryCategory]);

  // Managers — any user with admin/director/manager role
  const managers = useMemo(() => allUsers.filter(u => ['ptc_admin', 'ptc_director', 'ptc_manager', 'company_principal', 'company_admin', 'company_director', 'manager_director', 'company_manager'].includes(u.role)), [allUsers]);

  // v3.15-667 / v3.20-1503: Show internal users for admins, own company users
  // for external directors AND external managers.  Prior to v3.20-1503 the
  // filter only recognised `company_director`, so a `company_manager` fell
  // through to the internal-only branch and saw 0 users on the Employee
  // Management page.  The top-level counter (line ~2500) already handled
  // both roles — this now matches.
  const isExternalPartnerStaff =
    currentUser?.role === 'company_principal' ||
    currentUser?.role === 'company_admin' ||
    currentUser?.role === 'company_director' ||
    currentUser?.role === 'manager_director' ||
    currentUser?.role === 'company_manager';

  const groupedUsers = useMemo(() => {
    const groups = {};
    const visibleUsers = users.filter(u => {
      const comp = companies.find(co => co.name === u.company_name);
      if (isExternalPartnerStaff) {
        return u.company_id === currentUser?.company_id;
      }
      return comp?.company_type === 'internal';
    });
    // v3.15-668: Apply status filter
    const filteredUsers = visibleUsers.filter(u => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'pending') return u.approval_status === 'pending';
      if (statusFilter === 'active') return u.approval_status !== 'pending' && u.is_active !== false;
      return true;
    });
    filteredUsers.forEach(u => {
      const cn = u.company_name || 'Unknown';
      if (!groups[cn]) { const c = companies.find(co => co.name === cn); groups[cn] = { companyName: cn, companyId: c?.id || null, companyType: c?.company_type || 'internal', users: [] }; }
      groups[cn].users.push(u);
    });
    const arr = Object.values(groups);
    arr.sort((a, b) => a.companyName.localeCompare(b.companyName));
    return arr;
  }, [users, companies, isExternalPartnerStaff, currentUser, statusFilter]);

  useEffect(() => {
    if (groupedUsers.length > 0 && Object.keys(expandedCompanies).length === 0) {
      const init = {}; groupedUsers.forEach(g => { init[g.companyName] = g.companyType === 'internal'; }); setExpandedCompanies(init);
    }
  }, [groupedUsers]);

  useEffect(() => {
    if (companies.length > 0 && !newUser.company_id) {
      if (isExternalDirector) { setNewUser(p => ({ ...p, company_id: currentUser.company_id, role: 'company_team_member' })); }
      else { const def = companies.find(c => c.company_type === 'internal') || companies[0]; if (def) setNewUser(p => ({ ...p, company_id: def.id, role: 'ptc_team_member' })); }
    }
  }, [companies, currentUser, isExternalDirector]);

  const handleCompanyChange = (id) => { const c = companies.find(co => co.id === id); setNewUser({ ...newUser, company_id: id, role: c?.company_type === 'internal' ? 'ptc_team_member' : 'company_team_member' }); };

  const handleCreate = async (e, addAnother = false, overrideOpts = null) => {
    e && e.preventDefault && e.preventDefault();
    const cid = isExternalDirector ? currentUser.company_id : newUser.company_id;

    // v3.18-1321 — Duplicate check BEFORE creating the employee.
    // When ``overrideOpts.force_create_duplicate`` is set, the operator
    // already accepted the "Create Intentional Duplicate" action in the
    // duplicate dialog and provided a reason — we skip the pre-check and
    // pass the flag through.
    if (!overrideOpts && newUser.email && newUser.email.includes('@')) {
      try {
        const params = { email: newUser.email };
        if (cid) params.target_company_id = cid;
        const r = await api.get('/employees/duplicate-check', { params });
        if (r.data?.exists) {
          setDuplicatePrompt({
            email:              newUser.email,
            targetCompanyId:    cid,
            targetCompanyName:  r.data.target_company_name,
            incoming: {
              full_name:  newUser.full_name,
              phone:      newUser.phone,
              extension:  newUser.extension,
              position:   newUser.position,
            },
            addAnother,
          });
          return;
        }
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Duplicate check failed'));
        return;
      }
    }

    setCreating(true);
    try {
      const payload = {
        full_name: newUser.full_name, email: newUser.email,
        phone: newUser.phone || null, password: newUser.password,
        role: newUser.role, company_id: cid,
        extension: newUser.extension || null,
        position: newUser.position || null,
      };
      if (overrideOpts?.force_create_duplicate) {
        payload.force_create_duplicate = true;
        payload.duplicate_reason = overrideOpts.duplicate_reason || 'Intentional duplicate';
      }
      await api.post('/users', payload);
      toast.success('Team member added & entered onboarding');
      const keepRole = newUser.role;
      const keepCompany = newUser.company_id;
      setNewUser({ full_name: '', email: '', phone: '', password: '', role: keepRole, company_id: keepCompany, extension: '', position: '' });
      if (!addAnother) setIsCreateOpen(false);
      onRefresh();
    } catch (err) { toast.error(getApiErrorMessage(err, 'Failed to create team member')); } finally { setCreating(false); }
  };

  const handleBulkCreate = async () => {
    const lines = bulkUsernames.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    setBulkCreating(true);
    try {
      const cid = isExternalDirector ? currentUser.company_id : bulkCompanyId;
      const res = await api.post('/users/bulk', { usernames: lines, company_id: cid, role: bulkRole, temp_password: bulkPassword });
      setBulkResults(res.data);
      toast.success(`${res.data.created} users created`);
      onRefresh();
    } catch (err) { toast.error(getApiErrorMessage(err, 'Bulk creation failed')); } finally { setBulkCreating(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault(); if (!editingUser) return; setSaving(true);
    try {
      const updates = { full_name: editingUser.full_name, email: editingUser.email, phone: editingUser.phone || null, role: editingUser.role, is_active: editingUser.is_active, extension: editingUser.extension || null, position: editingUser.position || null, voicemail_greeting: editingUser.voicemail_greeting || null, company_routing: editingUser.company_routing || [], address_line_1: editingUser.address_line_1 || null, address_line_2: editingUser.address_line_2 || null, city: editingUser.city || null, state: editingUser.state || null, zip: editingUser.zip || null, company_id: editingUser.company_id || null };
      if (editingUser.newPassword) updates.password = editingUser.newPassword;
      await api.put(`/users/${editingUser.id}`, updates);
      toast.success('Team member updated'); setIsEditOpen(false); setEditingUser(null); onRefresh();
    } catch (err) { toast.error(getApiErrorMessage(err, 'Failed to update')); } finally { setSaving(false); }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try { await api.put(`/users/${userId}`, { is_active: !currentActive }); toast.success(currentActive ? 'User disabled' : 'User enabled'); onRefresh(); } catch (err) { toast.error(getApiErrorMessage(err, 'Failed')); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try { await api.put(`/users/${userId}`, { role: newRole }); toast.success('Role updated'); onRefresh(); } catch (err) { toast.error(getApiErrorMessage(err, 'Failed')); }
  };

  const handleManagerAssign = async (userId, managerId) => {
    const actualId = managerId === '__none__' ? '' : managerId;
    try { await api.put(`/hr/assign-manager/${userId}`, { manager_id: actualId }); toast.success('Manager assigned'); onRefresh(); } catch (err) { toast.error(getApiErrorMessage(err, 'Failed')); }
  };

  const handleDelete = async (id) => { try { await api.delete(`/users/${id}`); toast.success('Deleted'); onRefresh(); } catch (err) { toast.error(getApiErrorMessage(err, 'Failed')); } };

  return (
    <div className="space-y-4" data-testid="team-members-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-slate-500">{groupedUsers.reduce((sum, g) => sum + g.users.length, 0)} team members across {groupedUsers.length} companies</p>
          <div className="flex items-center gap-1 ml-2" data-testid="status-filter-group">
            {[{ value: 'all', label: 'All' }, { value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' }].map(f => (
              <button key={f.value} onClick={() => setStatusFilter(f.value)} data-testid={`status-filter-${f.value}`}
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${statusFilter === f.value ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
        {/* v3.18-1321 — Team Management shortcut */}
        <Button
          variant="outline" className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          onClick={() => navigate('/hr/teams')}
          data-testid="manage-teams-btn">
          <Briefcase className="h-3.5 w-3.5 mr-1" /> Manage Teams
        </Button>
        {/* v3.15-908: Bulk Send Credentials */}
        {selectedUserIds.size > 0 && (
          <Button
            variant="outline"
            className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={() => sendCredentialsForUsers(Array.from(selectedUserIds), 'bulk')}
            disabled={sendingBulkCreds}
            data-testid="bulk-send-credentials-users-btn"
          >
            {sendingBulkCreds ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
            Send Credentials to Selected ({selectedUserIds.size})
          </Button>
        )}
        <Dialog open={isBulkOpen} onOpenChange={(v) => { setIsBulkOpen(v); if (!v) setBulkResults(null); }}>
          <DialogTrigger asChild><Button variant="outline" className="text-xs" data-testid="bulk-add-btn"><Users className="h-3.5 w-3.5 mr-1" />Bulk Add</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Bulk Add Users</DialogTitle></DialogHeader>
            {bulkResults ? (
              <div className="space-y-3" data-testid="bulk-results">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-50 rounded p-2"><p className="text-lg font-bold text-green-700">{bulkResults.created}</p><p className="text-[10px] text-green-600">Created</p></div>
                  <div className="bg-amber-50 rounded p-2"><p className="text-lg font-bold text-amber-700">{bulkResults.skipped}</p><p className="text-[10px] text-amber-600">Skipped</p></div>
                  <div className="bg-red-50 rounded p-2"><p className="text-lg font-bold text-red-700">{bulkResults.errors}</p><p className="text-[10px] text-red-600">Errors</p></div>
                </div>
                {bulkResults.details?.created?.length > 0 && (
                  <div><p className="text-xs font-medium text-slate-600 mb-1">Created:</p>{bulkResults.details.created.map((u, i) => <p key={u.email || `created-${i}`} className="text-xs text-green-700">{u.email} — {u.full_name}</p>)}</div>
                )}
                {bulkResults.details?.skipped?.length > 0 && (
                  <div><p className="text-xs font-medium text-slate-600 mb-1">Skipped:</p>{bulkResults.details.skipped.map((u, i) => <p key={u.email || `skipped-${i}`} className="text-xs text-amber-700">{u.email} — {u.reason}</p>)}</div>
                )}
                <Button className="w-full bg-accent hover:bg-gold-dark" onClick={() => { setBulkResults(null); setBulkUsernames(''); setIsBulkOpen(false); }}>Done</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div><Label>Usernames (one per line)</Label><textarea className="w-full border rounded-md p-2 text-sm h-32 focus:outline-none focus:ring-2 focus:ring-accent" value={bulkUsernames} onChange={e => setBulkUsernames(e.target.value)} placeholder={"john.smith\njane.doe\nmike.jones"} data-testid="bulk-usernames-input" /></div>
                {!isExternalDirector && (
                  <div><Label>Company *</Label><Select value={bulkCompanyId} onValueChange={setBulkCompanyId}><SelectTrigger data-testid="bulk-company-select"><SelectValue placeholder="Select company" /></SelectTrigger><SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.email_domain ? ` (@${c.email_domain})` : ''}</SelectItem>)}</SelectContent></Select></div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Role</Label><Select value={bulkRole} onValueChange={setBulkRole}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{EXTERNAL_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Temp Password</Label><Input value={bulkPassword} onChange={e => setBulkPassword(e.target.value)} data-testid="bulk-password-input" /></div>
                </div>
                <p className="text-[10px] text-slate-400">Email domain will be auto-appended from company settings. All users will have must_change_password=true.</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsBulkOpen(false)}>Cancel</Button>
                  <Button className="bg-accent hover:bg-gold-dark" onClick={handleBulkCreate} disabled={bulkCreating || !bulkUsernames.trim() || (!isExternalDirector && !bulkCompanyId)} data-testid="bulk-submit-btn">
                    {bulkCreating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
                    Create {bulkUsernames.split('\n').filter(l => l.trim()).length} Users
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild><Button className="bg-accent hover:bg-gold-dark" data-testid="create-user-btn"><Plus className="h-4 w-4 mr-2" />Add Team Member</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Add Team Member</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div><Label>Full Name *</Label><Input value={newUser.full_name} onChange={e => setNewUser({ ...newUser, full_name: e.target.value })} required data-testid="user-name-input" autoFocus /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email *</Label>
                  <div className="relative">
                    <Input value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required data-testid="user-email-input" placeholder={emailDomain ? `username` : 'email@domain.com'} />
                    {emailDomain && !newUser.email.includes('@') && newUser.email.length > 0 && (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">@{emailDomain}</span>
                    )}
                  </div>
                  {emailDomain && <p className="text-[10px] text-slate-400 mt-0.5">Domain: @{emailDomain} (auto-appended)</p>}
                </div>
                <div><Label>Phone</Label><Input type="tel" value={newUser.phone} onChange={e => setNewUser({ ...newUser, phone: e.target.value })} data-testid="user-phone-input" /></div>
              </div>
              {/* v3.21-1634 · Block 2 · Defect B — Extension gated to PTC-internal callers. */}
              {isInternalCaller ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Extension</Label><Input maxLength={3} value={newUser.extension} onChange={e => setNewUser({ ...newUser, extension: e.target.value.replace(/\D/g, '').slice(0, 3) })} data-testid="user-extension-input" /></div>
                  <div><Label>Position</Label><Input value={newUser.position} onChange={e => setNewUser({ ...newUser, position: e.target.value })} data-testid="user-position-input" /></div>
                </div>
              ) : (
                <div><Label>Position</Label><Input value={newUser.position} onChange={e => setNewUser({ ...newUser, position: e.target.value })} data-testid="user-position-input" /></div>
              )}
              <div><Label>Password *</Label><Input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required minLength={6} data-testid="user-password-input" /></div>
              {isAdmin() ? (
                <div><Label>Company *</Label><Select value={newUser.company_id} onValueChange={handleCompanyChange}><SelectTrigger data-testid="user-company-select"><SelectValue placeholder="Select company" /></SelectTrigger><SelectContent>{companies.filter(c => c.company_type === 'internal').map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              ) : (<div><Label>Company</Label><Input value={currentUser?.company_name || ''} disabled className="bg-slate-50" /></div>)}
              <div><Label>Role *</Label><Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent>{availableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button type="button" variant="outline" className="text-accent border-accent hover:bg-amber-50" disabled={creating} onClick={(e) => handleCreate(e, true)} data-testid="save-add-another-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & Add Another'}
                </Button>
                <Button type="submit" className="bg-accent hover:bg-gold-dark" disabled={creating} data-testid="submit-user-btn">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Member'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card><CardContent className="p-0">
        {groupedUsers.map(group => (
          <div key={group.companyName} className="border-b last:border-0" data-testid={`company-section-${group.companyName.toLowerCase().replace(/\s/g, '-')}`}>
            <button className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors" onClick={() => setExpandedCompanies(p => ({ ...p, [group.companyName]: !p[group.companyName] }))}>
              <div className="flex items-center gap-2">
                {expandedCompanies[group.companyName] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                <Building2 className={`h-4 w-4 ${group.companyType === 'internal' ? 'text-purple-600' : 'text-slate-500'}`} />
                <span className={`font-semibold text-sm ${group.companyType === 'internal' ? 'text-purple-800' : 'text-slate-800'}`}>{group.companyName}</span>
                {group.companyType === 'internal' && <Badge className="bg-purple-100 text-purple-800 text-[10px] px-1.5 py-0" variant="secondary">Internal</Badge>}
              </div>
              <span className="text-xs text-slate-400">{group.users.length}</span>
            </button>
            {expandedCompanies[group.companyName] && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-slate-50/70">
                    {/* v3.15-908: select column */}
                    <th className="w-10 py-2 pl-3 pr-1 text-left">
                      <input
                        type="checkbox"
                        checked={group.users.length > 0 && group.users.every(u => selectedUserIds.has(u.id))}
                        onChange={() => {
                          const all = group.users.length > 0 && group.users.every(u => selectedUserIds.has(u.id));
                          group.users.forEach(u => {
                            const isSel = selectedUserIds.has(u.id);
                            if (all && isSel) toggleSelectUser(u.id);
                            else if (!all && !isSel) toggleSelectUser(u.id);
                          });
                        }}
                        className="h-3.5 w-3.5 align-middle"
                        data-testid={`select-all-users-${group.companyName.toLowerCase().replace(/\s/g, '-')}`}
                      />
                    </th>
                    {['Name','Email','Phone','Role','Status','Manager','Start Date','Actions'].map(h => (
                      <th key={h} className={`py-2 px-3 text-[10px] font-medium text-slate-500 uppercase ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {group.users.map(u => (
                      <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                        {/* v3.15-908: per-row select */}
                        <td className="py-2 pl-3 pr-1">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(u.id)}
                            onChange={() => toggleSelectUser(u.id)}
                            className="h-3.5 w-3.5 align-middle"
                            data-testid={`select-user-${u.id}`}
                          />
                        </td>
                        <td className="py-2 px-3"><div className="flex items-center gap-2"><div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-semibold ${group.companyType === 'internal' ? 'bg-purple-600' : 'bg-accent'}`}>{u.full_name?.charAt(0) || 'U'}</div><span className="text-sm font-medium text-slate-800">{u.full_name}</span></div></td>
                        <td className="py-2 px-3"><a href={`mailto:${u.email}`} className="text-xs text-blue-600 hover:underline">{u.email}</a></td>
                        <td className="py-2 px-3 text-xs text-slate-600">
                          {u.phone ? u.phone : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium" data-testid={`missing-phone-badge-${u.id}`}><Phone className="h-3 w-3" />Missing Phone</span>}
                        </td>
                        <td className="py-2 px-3">
                          <Select value={u.role} onValueChange={v => handleRoleChange(u.id, v)}>
                            <SelectTrigger className="h-6 text-[10px] w-auto border-0 bg-transparent p-0 shadow-none" data-testid={`role-select-${u.id}`}>
                              <Badge className={`${roleColors[u.role] || 'bg-slate-100'} text-[10px] px-1.5 py-0 cursor-pointer`} variant="secondary">{formatRole(u.role)}</Badge>
                            </SelectTrigger>
                            <SelectContent>{((() => {
                              if (group.companyType === 'internal') return INTERNAL_ROLES;
                              const c = companies.find(co => co.id === group.companyId);
                              // v3.15-962: FAIL-OPEN inline role select.
                              const ic = (c?.industry_category || '').toLowerCase();
                              const knownNonTs = ['solar_sales','solar_exit','travel_club_sales','automotive'];
                              if (ic === 'timeshare_exit' || !ic) return [...EXTERNAL_ROLES, VO_OFFICER_ROLE];
                              if (knownNonTs.includes(ic)) return EXTERNAL_ROLES;
                              return [...EXTERNAL_ROLES, VO_OFFICER_ROLE];
                            })()).map(r => <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 px-3">
                          {u.approval_status === 'pending' ? (
                            <div className="flex items-center gap-1.5">
                              <Badge className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0" variant="secondary" data-testid={`status-badge-${u.id}`}>Pending</Badge>
                              {currentUser?.role === 'ptc_admin' && (
                                <>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await api.post(`/users/${u.id}/approve`);
                                        toast.success(`${u.full_name} approved`);
                                        fetchData();
                                      } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to approve'); }
                                    }}
                                    className="text-[10px] px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                    data-testid={`approve-user-${u.id}`}
                                  >Approve</button>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await api.post(`/users/${u.id}/reject`);
                                        toast.success(`${u.full_name} declined`);
                                        fetchData();
                                      } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to decline'); }
                                    }}
                                    className="text-[10px] px-2 py-0.5 bg-red-500 text-white rounded hover:bg-red-600 font-medium"
                                    data-testid={`decline-user-${u.id}`}
                                  >Decline</button>
                                </>
                              )}
                            </div>
                          ) : u.approval_status === 'rejected' ? (
                            <Badge className="bg-red-100 text-red-800 text-[10px] px-1.5 py-0" variant="secondary" data-testid={`status-badge-${u.id}`}>Declined</Badge>
                          ) : (
                            <button onClick={() => handleToggleActive(u.id, u.is_active)} className="cursor-pointer" data-testid={`toggle-active-${u.id}`}>
                              <Badge className={`text-[10px] px-1.5 py-0 cursor-pointer ${u.is_active ? 'bg-green-100 text-green-800 hover:bg-red-100 hover:text-red-800' : 'bg-red-100 text-red-800 hover:bg-green-100 hover:text-green-800'}`} variant="secondary" data-testid={`status-badge-${u.id}`}>
                                {u.is_active ? 'Active' : 'Disabled'}
                              </Badge>
                            </button>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <Select value={u.manager_id || '__none__'} onValueChange={v => handleManagerAssign(u.id, v)}>
                            <SelectTrigger className="h-6 text-[10px] w-[120px] border-dashed" data-testid={`manager-select-${u.id}`}>
                              <SelectValue placeholder="Assign...">{u.manager_name || 'Assign...'}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__" className="text-xs text-slate-400">None</SelectItem>
                              {managers.filter(m => m.id !== u.id).map(m => <SelectItem key={m.id} value={m.id} className="text-xs">{m.full_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 px-3 text-xs text-slate-500">{u.start_date ? formatET(u.start_date, 'MMM d, yy') : u.created_at ? formatET(u.created_at, 'MMM d, yy') : '-'}</td>
                        <td className="py-2 px-3">
                          <div className="flex items-center justify-end gap-0.5">
                            {/* v3.15-908: Send / Resend Credentials */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`h-6 w-6 ${u.credentials_sent ? 'text-blue-600' : 'text-emerald-700'}`}
                              title={u.credentials_last_sent_at
                                ? `Last sent ${formatET(u.credentials_last_sent_at, 'MMM d, yyyy h:mm a')}. Click to resend.`
                                : (u.email ? 'Send welcome credentials' : 'No email on file')}
                              onClick={() => sendCredentialsForUsers([u.id], 'single')}
                              disabled={sendingUserIds.has(u.id) || !u.email}
                              data-testid={`send-credentials-user-${u.id}`}
                            >
                              {sendingUserIds.has(u.id) ? <Loader2 className="h-3 w-3 animate-spin" /> : (u.credentials_sent ? <MailCheck className="h-3 w-3" /> : <Mail className="h-3 w-3" />)}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" title="Edit Profile" onClick={() => { setEditingUser({ ...u, newPassword: '' }); setIsEditOpen(true); }} data-testid={`edit-user-${u.id}`}><Edit className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" title="View Activity" onClick={() => navigate(`/clients?search=${encodeURIComponent(u.full_name)}`)} data-testid={`view-activity-${u.id}`}><Eye className="h-3 w-3" /></Button>
                            {u.id !== currentUser?.id && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-6 w-6 text-red-600" data-testid={`delete-user-${u.id}`}><Trash2 className="h-3 w-3" /></Button></AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader><AlertDialogTitle>Remove {u.full_name}?</AlertDialogTitle><AlertDialogDescription>This permanently deletes the user.</AlertDialogDescription></AlertDialogHeader>
                                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(u.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </CardContent></Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(v) => { setIsEditOpen(v); if (!v) setShowAssignmentsForUser(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Edit Team Member</DialogTitle></DialogHeader>
          {editingUser && (
            <form onSubmit={handleUpdate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full Name *</Label><Input value={editingUser.full_name} onChange={e => setEditingUser({ ...editingUser, full_name: e.target.value })} required data-testid="edit-user-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={editingUser.email} onChange={e => setEditingUser({ ...editingUser, email: e.target.value })} required data-testid="edit-user-email" /></div>
              </div>
              <div className={isInternalCaller ? "grid grid-cols-3 gap-3" : "grid grid-cols-2 gap-3"}>
                <div><Label>Phone</Label><Input type="tel" value={editingUser.phone || ''} onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })} data-testid="edit-user-phone" /></div>
                {/* v3.21-1634 · Block 2 · Defect B — Extension gated to PTC-internal callers. */}
                {isInternalCaller && (
                  <div><Label>Extension</Label><Input maxLength={3} value={editingUser.extension || ''} onChange={e => setEditingUser({ ...editingUser, extension: e.target.value.replace(/\D/g, '').slice(0, 3) })} /></div>
                )}
                <div><Label>Position</Label><Input value={editingUser.position || ''} onChange={e => setEditingUser({ ...editingUser, position: e.target.value })} /></div>
                <div className="col-span-2 border-t pt-2 mt-1">
                  <Label className="text-[10px] text-slate-400 uppercase">Mailing Address (for payroll)</Label>
                </div>
                <div className="col-span-2"><Label>Address Line 1</Label><Input value={editingUser.address_line_1 || ''} onChange={e => setEditingUser({ ...editingUser, address_line_1: e.target.value })} placeholder="Street address" data-testid="user-address1" /></div>
                <div className="col-span-2"><Label>Address Line 2</Label><Input value={editingUser.address_line_2 || ''} onChange={e => setEditingUser({ ...editingUser, address_line_2: e.target.value })} placeholder="Apt, Suite (optional)" /></div>
                <div><Label>City</Label><Input value={editingUser.city || ''} onChange={e => setEditingUser({ ...editingUser, city: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-1">
                  <div><Label>State</Label><Input value={editingUser.state || ''} onChange={e => setEditingUser({ ...editingUser, state: e.target.value })} maxLength={2} /></div>
                  <div><Label>Zip</Label><Input value={editingUser.zip || ''} onChange={e => setEditingUser({ ...editingUser, zip: e.target.value })} maxLength={10} /></div>
                </div>
                {!editingUser.address_line_1 && <div className="col-span-2"><p className="text-[10px] text-amber-500">Address required for payroll documents</p></div>}
              </div>
              {/* v3.21-1634 · Block 2 · Defect B — Voicemail Greeting gated to PTC-internal callers. */}
              {isInternalCaller && (
                <div><Label>Voicemail Greeting</Label><Textarea value={editingUser.voicemail_greeting || ''} onChange={e => setEditingUser({ ...editingUser, voicemail_greeting: e.target.value })} rows={2} className="text-sm resize-none" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>New Password (blank = keep)</Label><Input type="password" value={editingUser.newPassword} onChange={e => setEditingUser({ ...editingUser, newPassword: e.target.value })} minLength={6} /></div>
                <div><Label>Role *</Label><Select value={editingUser.role} onValueChange={v => setEditingUser({ ...editingUser, role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{editingAvailableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
              </div>
              {currentUser?.role === 'ptc_admin' && (
                <div>
                  <Label>Company</Label>
                  <Select value={editingUser.company_id || ''} onValueChange={v => { const c = companies.find(co => co.id === v); setEditingUser({ ...editingUser, company_id: v, company_name: c?.name || '' }); }}>
                    <SelectTrigger data-testid="edit-user-company-select"><SelectValue placeholder="Select company">{editingUser.company_name || 'Select company'}</SelectValue></SelectTrigger>
                    <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-between border rounded-lg p-3 bg-slate-50/50">
                <div className="flex items-center gap-2"><Power className="h-4 w-4 text-slate-500" /><Label>Active</Label></div>
                <Switch checked={editingUser.is_active} onCheckedChange={v => setEditingUser({ ...editingUser, is_active: v })} />
              </div>
              {currentUser?.role === 'ptc_admin' && (
                <div className="flex items-center justify-between border rounded-lg p-3 bg-slate-50/50">
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-500" /><Label>Phone System</Label></div>
                  <Switch checked={Array.isArray(editingUser.company_routing) && editingUser.company_routing.length > 0} onCheckedChange={checked => { if (checked) setEditingUser({ ...editingUser, company_routing: ['eqf_hub'] }); else setEditingUser({ ...editingUser, company_routing: [] }); }} data-testid="edit-user-phone-system-toggle" />
                </div>
              )}
              {editingUser.id && (
                <div className="border-t pt-3">
                  <button
                    type="button"
                    onClick={() => setShowAssignmentsForUser(v => !v)}
                    className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-500 hover:text-slate-800 pb-2"
                    data-testid="edit-user-assignments-toggle">
                    <span className="font-semibold">Company Memberships &amp; Operational Assignments</span>
                    <span>{showAssignmentsForUser ? '▾' : '▸'}</span>
                  </button>
                  {showAssignmentsForUser && (
                    <>
                      <EmployeeAssignmentsPanel userId={editingUser.id} />
                      <div className="mt-4 pt-3 border-t">
                        <OperationalRolesPanel userId={editingUser.id} />
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-2 border-t">
                {/* v3.15-910: Reset & Resend — fresh temp password + welcome email, in-place. */}
                <Button
                  type="button"
                  variant="outline"
                  className={editingUser.credentials_sent ? 'border-blue-300 text-blue-700 hover:bg-blue-50' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}
                  disabled={!editingUser?.email || sendingUserIds.has(editingUser?.id)}
                  onClick={async () => {
                    if (!editingUser?.id) return;
                    await sendCredentialsForUsers([editingUser.id], 'single');
                    // Reflect the freshly-sent state immediately in the open dialog.
                    setEditingUser((cur) => cur ? { ...cur, credentials_sent: true, credentials_last_sent_at: new Date().toISOString() } : cur);
                  }}
                  data-testid="reset-resend-credentials-btn"
                  title={editingUser?.credentials_last_sent_at ? `Last sent ${formatET(editingUser.credentials_last_sent_at, 'MMM d, yyyy h:mm a')}` : 'Reset password and email new credentials'}
                >
                  {sendingUserIds.has(editingUser?.id)
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : (editingUser.credentials_sent ? <MailCheck className="h-4 w-4 mr-2" /> : <Mail className="h-4 w-4 mr-2" />)}
                  Reset &amp; Resend Credentials
                </Button>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); setEditingUser(null); }}>Cancel</Button>
                  <Button type="submit" className="bg-accent hover:bg-gold-dark" disabled={saving} data-testid="save-user-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}</Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      {/* v3.18-1321 — Duplicate resolution + Team Management shortcut */}
      <EmployeeDuplicateDialog
        open={!!duplicatePrompt}
        onOpenChange={(v) => { if (!v) setDuplicatePrompt(null); }}
        email={duplicatePrompt?.email}
        targetCompanyId={duplicatePrompt?.targetCompanyId}
        targetCompanyName={duplicatePrompt?.targetCompanyName}
        incoming={duplicatePrompt?.incoming || {}}
        onUseExisting={(employee) => {
          // Open the existing employee in the edit dialog.
          const u = employee?._id_for_actions ? users.find(x => x.id === employee._id_for_actions) : null;
          if (u) {
            setEditingUser({ ...u, newPassword: '' });
            setIsCreateOpen(false);
            setIsEditOpen(true);
          }
        }}
        onDone={(overrideOpts) => {
          const addAnother = !!duplicatePrompt?.addAnother;
          setDuplicatePrompt(null);
          if (overrideOpts?.force_create_duplicate) {
            // Admin authorised an intentional duplicate — proceed with create.
            handleCreate(null, addAnother, overrideOpts);
          } else {
            // Non-create branch (add company / move / merge) — refresh list & close create.
            setIsCreateOpen(false);
            onRefresh();
          }
        }}
      />
    </div>
  );
};

// =================== ONBOARDING TAB (DRAG & DROP) ===================
const OnboardingTab = ({ onRefresh }) => {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCard, setActiveCard] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    const fetchOnboarding = async () => {
      try { const res = await api.get('/hr/onboarding'); setPipeline(res.data || []); } catch { setPipeline([]); } finally { setLoading(false); }
    };
    fetchOnboarding();
  }, []);

  const stageGroups = useMemo(() => {
    const groups = {};
    ONBOARDING_STAGES.forEach(s => { groups[s] = []; });
    pipeline.forEach(p => { const stage = p.stage || 'Applied'; if (groups[stage]) groups[stage].push(p); });
    return groups;
  }, [pipeline]);

  const moveStage = async (userId, newStage) => {
    try {
      await api.put(`/hr/onboarding/${userId}`, { stage: newStage });
      setPipeline(p => p.map(item => item.user_id === userId ? { ...item, stage: newStage, [`${newStage.toLowerCase()}_at`]: new Date().toISOString() } : item));
      toast.success(`Moved to ${newStage}`);
      if (newStage === 'Docs' || newStage === 'Training') onRefresh();
    } catch { toast.error('Failed to update stage'); }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveCard(null);
    if (!over || !active) return;
    const overId = over.id;
    const stage = ONBOARDING_STAGES.find(s => overId === `stage-${s}` || overId.startsWith(`card-`));
    // Find which stage the card was dropped into
    for (const s of ONBOARDING_STAGES) {
      const el = document.querySelector(`[data-stage="${s}"]`);
      if (el) {
        const rect = el.getBoundingClientRect();
        // Check if the over element is within this stage column
        if (stageGroups[s].some(item => `card-${item.user_id}` === overId)) {
          moveStage(active.id.replace('card-', ''), s);
          return;
        }
      }
    }
    // If dropped on a stage column directly
    if (overId.startsWith('stage-')) {
      moveStage(active.id.replace('card-', ''), overId.replace('stage-', ''));
    }
  };

  const stageColors = { Applied: 'bg-blue-50 border-blue-200', Interview: 'bg-amber-50 border-amber-200', Approved: 'bg-emerald-50 border-emerald-200', Docs: 'bg-purple-50 border-purple-200', Training: 'bg-indigo-50 border-indigo-200', Active: 'bg-green-50 border-green-200' };
  const stageIcons = { Applied: ClipboardList, Interview: Users, Approved: CheckCircle, Docs: FileText, Training: GraduationCap, Active: Award };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4" data-testid="onboarding-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{pipeline.length} in onboarding pipeline</p>
      </div>
      <div className="grid grid-cols-6 gap-2">
        {ONBOARDING_STAGES.map((stage, idx) => {
          const StageIcon = stageIcons[stage];
          return (
            <div key={stage} className={`rounded-lg border-2 p-2 min-h-[220px] ${stageColors[stage]}`} data-stage={stage} data-testid={`stage-${stage.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1">
                  <StageIcon className="h-3.5 w-3.5 text-slate-600" />
                  <h3 className="text-xs font-bold text-slate-700">{stage}</h3>
                </div>
                <Badge variant="outline" className="text-[10px] px-1 py-0 font-bold">{stageGroups[stage].length}</Badge>
              </div>
              <div className="space-y-1.5">
                {stageGroups[stage].map(item => (
                  <div key={item.user_id} className="bg-white rounded-lg border p-2 shadow-sm hover:shadow-md transition-shadow cursor-pointer group" data-testid={`onboarding-card-${item.user_id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-800 truncate">{item.user_name}</p>
                    </div>
                    <p className="text-[10px] text-slate-500">{formatRole(item.role)}</p>
                    {item.updated_at && <p className="text-[9px] text-slate-400 mt-0.5">{formatET(item.updated_at, 'MMM d, h:mm a')}</p>}
                    <div className="flex gap-1 mt-1.5 opacity-80 group-hover:opacity-100">
                      {idx < ONBOARDING_STAGES.length - 1 && (
                        <Button size="sm" variant="outline" className="h-5 text-[9px] px-1.5 flex-1" onClick={(e) => { e.stopPropagation(); moveStage(item.user_id, ONBOARDING_STAGES[idx + 1]); }} data-testid={`move-next-${item.user_id}`}>
                          <ArrowRight className="h-2.5 w-2.5 mr-0.5" />{ONBOARDING_STAGES[idx + 1]}
                        </Button>
                      )}
                      {stage === 'Docs' && (
                        <Button size="sm" variant="outline" className="h-5 text-[9px] px-1.5 text-purple-600" onClick={(e) => { e.stopPropagation(); toast.success('Documents sent'); }}>
                          <Send className="h-2.5 w-2.5 mr-0.5" />Send
                        </Button>
                      )}
                      {stage === 'Training' && (
                        <Button size="sm" variant="outline" className="h-5 text-[9px] px-1.5 text-green-600" onClick={(e) => { e.stopPropagation(); moveStage(item.user_id, 'Active'); }} data-testid={`mark-complete-${item.user_id}`}>
                          <CheckCircle className="h-2.5 w-2.5 mr-0.5" />Complete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =================== DOCUMENTS TAB ===================
// v3.18-1345 A1 — HrDocumentUploadDialog. Attaches a signed file to
// an existing hr_documents row using the ADMIN-007-style immutable
// version pattern on the backend. Includes optional notes, size guard,
// and version history preview after upload.
const HR_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const HrDocumentUploadDialog = ({ open, onOpenChange, doc, userName, docTypeLabel, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [markSigned, setMarkSigned] = useState(true);

  useEffect(() => {
    if (!open) {
      setFile(null); setNotes(''); setVersions([]); setMarkSigned(true);
      return;
    }
    if (!doc?.id) return;
    setLoadingVersions(true);
    api.get(`/hr/documents/${doc.id}/versions`)
      .then(res => setVersions(res.data?.items || []))
      .catch(() => setVersions([]))
      .finally(() => setLoadingVersions(false));
  }, [open, doc?.id]);

  const doUpload = async () => {
    if (!file) { toast.error('Pick a file first'); return; }
    if (file.size > HR_MAX_UPLOAD_BYTES) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`);
      return;
    }
    if (!doc?.id) { toast.error('Document row missing'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (notes.trim()) fd.append('notes', notes.trim());
      fd.append('mark_signed', markSigned ? 'true' : 'false');
      const res = await api.post(`/hr/documents/${doc.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded v${res.data?.current_version_number || '1'}${markSigned ? ' · marked as Signed' : ''}`);
      onUploaded?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadVersion = async (versionId, fileName) => {
    try {
      const res = await api.get(`/hr/documents/${doc.id}/versions/${versionId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="hr-doc-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            Upload Signed Document
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            {userName} · {docTypeLabel}
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">File</Label>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
              data-testid="hr-doc-upload-file-input"
            />
            {file && (
              <p className="text-[11px] text-slate-500 mt-1">
                {file.name} · {(file.size / 1024).toFixed(1)} KB · {file.type || 'unknown'}
              </p>
            )}
            <p className="text-[10px] text-slate-400 mt-1">
              Max 10 MB · PDF, images, or Word documents.
            </p>
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Countersigned by HR on Feb 25"
              rows={2}
              className="mt-1 text-xs"
              data-testid="hr-doc-upload-notes"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={markSigned}
              onChange={(e) => setMarkSigned(e.target.checked)}
              data-testid="hr-doc-upload-mark-signed"
            />
            Mark this document as <strong>Signed</strong> after upload
          </label>

          {(versions.length > 0 || loadingVersions) && (
            <div className="border-t pt-3">
              <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wide mb-2">
                Version history
              </p>
              {loadingVersions ? (
                <div className="text-xs text-slate-400 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {versions.map(v => (
                    <div key={v.id} className="flex items-center justify-between text-xs border rounded px-2 py-1 bg-slate-50" data-testid={`hr-doc-version-${v.version_number}`}>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">v{v.version_number}</span>
                        <span className="text-slate-500 ml-2 truncate">{v.file_name}</span>
                        <span className="text-slate-400 ml-2 text-[10px]">
                          {formatET(v.uploaded_at, 'M/d/yy p')}
                        </span>
                      </div>
                      <button
                        onClick={() => downloadVersion(v.id, v.file_name)}
                        className="text-blue-600 hover:text-blue-700 text-[11px] font-medium ml-2"
                        data-testid={`hr-doc-version-download-${v.version_number}`}
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={doUpload}
            disabled={!file || uploading}
            data-testid="hr-doc-upload-submit"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DocumentsTab = ({ users }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  // v3.18-1345 A1 — real upload dialog state.
  const [uploadTarget, setUploadTarget] = useState(null);
    // { doc, userName, docTypeLabel, userId, docType, docName }

  const fetchDocs = useCallback(async () => {
    try { const res = await api.get('/hr/documents'); setDocuments(res.data || []); } catch { setDocuments([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const updateStatus = async (docId, newStatus) => {
    try {
      await api.put(`/hr/documents/${docId}`, { status: newStatus });
      setDocuments(d => d.map(doc => doc.id === docId ? { ...doc, status: newStatus, updated_at: new Date().toISOString() } : doc));
      toast.success(`Marked as ${newStatus}`);
    } catch { toast.error('Failed to update'); }
  };

  const createAndSetStatus = async (userId, docType, docName, status) => {
    try {
      const res = await api.post('/hr/documents', { user_id: userId, doc_type: docType, doc_name: docName, status });
      setDocuments(d => [...d, res.data]);
      toast.success(`${docName} marked as ${status}`);
    } catch { toast.error('Failed'); }
  };

  const statusConfig = {
    pending: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
    signed: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    expired: { color: 'bg-red-100 text-red-800 border-red-200', icon: null },
    not_sent: { color: 'bg-slate-100 text-slate-500 border-slate-200', icon: null },
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4" data-testid="documents-tab">
      <div className="flex items-center gap-3">
        <p className="text-sm text-slate-500">{documents.length} document records</p>
        <div className="flex gap-1.5 ml-auto">
          {DEFAULT_DOCUMENTS.map(d => <Badge key={d.type} variant="outline" className="text-[10px]"><FileText className="h-2.5 w-2.5 mr-1" />{d.name}</Badge>)}
        </div>
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full">
          <thead><tr className="border-b bg-slate-50">
            <th className="text-left py-2 px-3 text-[10px] font-medium text-slate-500 uppercase">Team Member</th>
            {DEFAULT_DOCUMENTS.map(d => <th key={d.type} className="text-center py-2 px-3 text-[10px] font-medium text-slate-500 uppercase w-[140px]">{d.name}</th>)}
          </tr></thead>
          <tbody>
            {users.map(u => {
              const userDocs = documents.filter(d => d.user_id === u.id);
              return (
                <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-2.5 px-3"><div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-white text-[10px] font-semibold">{u.full_name?.charAt(0)}</div><span className="text-sm font-medium text-slate-800">{u.full_name}</span></div></td>
                  {DEFAULT_DOCUMENTS.map(docType => {
                    const doc = userDocs.find(d => d.doc_type === docType.type);
                    const status = doc?.status || 'not_sent';
                    const cfg = statusConfig[status] || statusConfig.not_sent;
                    const StatusIcon = cfg.icon;
                    return (
                      <td key={docType.type} className="py-2.5 px-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="flex items-center gap-1 mx-auto" data-testid={`doc-status-${u.id}-${docType.type}`}>
                                <Badge className={`${cfg.color} text-[10px] px-2 py-0.5 cursor-pointer border`} variant="secondary">
                                  {StatusIcon && <StatusIcon className="h-2.5 w-2.5 mr-0.5" />}
                                  {status === 'not_sent' ? 'Not Sent' : status.charAt(0).toUpperCase() + status.slice(1)}
                                </Badge>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="center">
                              <DropdownMenuItem className="text-xs" onClick={() => doc ? updateStatus(doc.id, 'pending') : createAndSetStatus(u.id, docType.type, docType.name, 'pending')}>
                                <Clock className="h-3 w-3 mr-2 text-amber-500" />Mark Pending
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-xs" onClick={() => doc ? updateStatus(doc.id, 'signed') : createAndSetStatus(u.id, docType.type, docType.name, 'signed')}>
                                <CheckCircle className="h-3 w-3 mr-2 text-green-500" />Mark Signed
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-xs"
                                data-testid={`doc-upload-open-${u.id}-${docType.type}`}
                                onClick={async () => {
                                  // Ensure an hr_documents row exists first — create in
                                  // "pending" if the operator hits Upload before Send.
                                  let target = doc;
                                  if (!target) {
                                    try {
                                      const res = await api.post('/hr/documents', {
                                        user_id: u.id, doc_type: docType.type, doc_name: docType.name, status: 'pending',
                                      });
                                      target = res.data;
                                      setDocuments(d => [...d, target]);
                                    } catch {
                                      toast.error('Could not create document row');
                                      return;
                                    }
                                  }
                                  setUploadTarget({
                                    doc: target,
                                    userName: u.full_name,
                                    docTypeLabel: docType.name,
                                  });
                                }}
                              >
                                <Upload className="h-3 w-3 mr-2 text-blue-500" />Upload Document
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {doc?.updated_at && <span className="text-[9px] text-slate-400">{formatET(doc.updated_at, 'M/d/yy')}</span>}
                          {doc?.current_version_number ? (
                            <span
                              className="text-[9px] text-blue-600 hover:text-blue-700 cursor-pointer underline underline-offset-2"
                              data-testid={`doc-file-open-${u.id}-${docType.type}`}
                              onClick={() => setUploadTarget({ doc, userName: u.full_name, docTypeLabel: docType.name })}
                              title="View / add version"
                            >
                              v{doc.current_version_number} file
                            </span>
                          ) : null}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent></Card>
      <HrDocumentUploadDialog
        open={!!uploadTarget}
        onOpenChange={(o) => { if (!o) setUploadTarget(null); }}
        doc={uploadTarget?.doc}
        userName={uploadTarget?.userName}
        docTypeLabel={uploadTarget?.docTypeLabel}
        onUploaded={(res) => {
          // Refresh documents list so the file badge appears without a full reload.
          fetchDocs();
        }}
      />
    </div>
  );
};

// =================== TRAINING TAB ===================
const TrainingTab = ({ users }) => {
  const [training, setTraining] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTraining = useCallback(async () => {
    try { const res = await api.get('/hr/training'); setTraining(res.data || []); } catch { setTraining([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTraining(); }, [fetchTraining]);

  const updateProgress = async (recordId, newPct) => {
    try {
      await api.put(`/hr/training/${recordId}`, { completed_pct: newPct });
      setTraining(t => t.map(item => item.id === recordId ? { ...item, completed_pct: newPct, status: newPct >= 100 ? 'completed' : newPct > 0 ? 'in_progress' : 'not_started' } : item));
    } catch { toast.error('Failed to update'); }
  };

  const statusColors = { completed: 'bg-green-100 text-green-800', in_progress: 'bg-blue-100 text-blue-800', not_started: 'bg-slate-100 text-slate-600' };

  // Group by user
  const byUser = useMemo(() => {
    const groups = {};
    training.forEach(t => { if (!groups[t.user_id]) groups[t.user_id] = { name: t.user_name, modules: [] }; groups[t.user_id].modules.push(t); });
    return Object.entries(groups);
  }, [training]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4" data-testid="training-tab">
      <p className="text-sm text-slate-500">{training.length} training assignments across {byUser.length} members</p>
      {byUser.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-slate-400">No training assignments yet. Move users to "Training" stage in Onboarding to auto-assign modules.</CardContent></Card>
      ) : byUser.map(([userId, data]) => (
        <Card key={userId}>
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><GraduationCap className="h-4 w-4 text-indigo-600" />{data.name}</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="space-y-2">
              {data.modules.map(m => (
                <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50/50 border" data-testid={`training-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800">{m.module_name}</p>
                    <p className="text-[10px] text-slate-400">{m.category} {m.assigned_at ? `| Assigned ${formatET(m.assigned_at, 'MMM d')}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 w-[200px]">
                    <Slider
                      value={[m.completed_pct || 0]}
                      onValueCommit={(val) => updateProgress(m.id, val[0])}
                      max={100}
                      step={10}
                      className="flex-1"
                    />
                    <span className="text-xs font-semibold w-10 text-right">{m.completed_pct || 0}%</span>
                  </div>
                  <Badge className={`${statusColors[m.status] || statusColors.not_started} text-[10px] px-1.5 py-0 w-[80px] justify-center`} variant="secondary">
                    {m.status === 'completed' ? 'Complete' : m.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                  </Badge>
                  {m.status !== 'completed' && (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-green-600" onClick={() => updateProgress(m.id, 100)} data-testid={`complete-training-${m.id}`}>
                      <CheckCircle className="h-3 w-3 mr-0.5" />Done
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// =================== PERFORMANCE TAB ===================
const PerformanceTab = () => {
  const [performance, setPerformance] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try { const res = await api.get(`/hr/performance?period=${period}`); setPerformance(res.data || []); } catch { setPerformance([]); } finally { setLoading(false); }
    };
    fetch();
  }, [period]);

  const totals = useMemo(() => ({
    deals: performance.reduce((s, p) => s + p.deals_closed, 0),
    revenue: performance.reduce((s, p) => s + p.revenue, 0),
    avg_conversion: performance.length > 0 ? performance.reduce((s, p) => s + p.conversion_pct, 0) / performance.length : 0,
  }), [performance]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4" data-testid="performance-tab">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-2.5 flex items-center gap-2"><Briefcase className="h-4 w-4 text-emerald-600" /><div><p className="text-[10px] text-slate-500">Deals Closed</p><p className="text-lg font-bold text-emerald-700">{totals.deals}</p></div></CardContent></Card>
          <Card className="bg-blue-50 border-blue-200"><CardContent className="p-2.5 flex items-center gap-2"><DollarSign className="h-4 w-4 text-blue-600" /><div><p className="text-[10px] text-slate-500">Total Revenue</p><p className="text-lg font-bold text-blue-700">${totals.revenue.toLocaleString()}</p></div></CardContent></Card>
          <Card className="bg-amber-50 border-amber-200"><CardContent className="p-2.5 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-amber-600" /><div><p className="text-[10px] text-slate-500">Avg Conversion</p><p className="text-lg font-bold text-amber-700">{totals.avg_conversion.toFixed(1)}%</p></div></CardContent></Card>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          {[{ key: 'weekly', label: 'Last 7 Days' }, { key: 'monthly', label: 'Last 30 Days' }].map(p => (
            <Button key={p.key} size="sm" variant={period === p.key ? 'default' : 'ghost'} className={`h-7 text-xs ${period === p.key ? '' : 'text-slate-500'}`} onClick={() => setPeriod(p.key)} data-testid={`period-${p.key}`}>
              {p.label}
            </Button>
          ))}
        </div>
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full">
          <thead><tr className="border-b bg-slate-50">
            <th className="text-left py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">#</th>
            <th className="text-left py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Rep</th>
            <th className="text-right py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Deals Closed</th>
            <th className="text-right py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Revenue</th>
            <th className="text-right py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Conversion</th>
            <th className="text-right py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Avg Deal</th>
          </tr></thead>
          <tbody>
            {performance.length === 0 ? (
              <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">No performance data for this period.</td></tr>
            ) : performance.map((p, i) => (
              <tr key={p.user_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="py-2.5 px-4 text-sm font-bold text-slate-400">{i + 1}</td>
                <td className="py-2.5 px-4"><div className="flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-white text-[10px] font-semibold">{p.user_name?.charAt(0)}</div><span className="text-sm font-medium text-slate-800">{p.user_name}</span></div></td>
                <td className="py-2.5 px-4 text-right text-sm font-bold text-slate-800">{p.deals_closed}</td>
                <td className="py-2.5 px-4 text-right text-sm font-bold text-emerald-600">${(p.revenue || 0).toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <div className="w-16 bg-slate-200 rounded-full h-1.5"><div className="h-1.5 rounded-full" style={{ width: `${Math.min(p.conversion_pct, 100)}%`, backgroundColor: p.conversion_pct >= 30 ? '#16a34a' : p.conversion_pct >= 15 ? '#d97706' : '#dc2626' }} /></div>
                    <span className="text-xs font-semibold">{(p.conversion_pct || 0).toFixed(1)}%</span>
                  </div>
                </td>
                <td className="py-2.5 px-4 text-right text-sm text-slate-600">${(p.avg_deal || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
};

// =================== PAYROLL TAB (READ-ONLY) ===================
// v3.15-1035: Commission Integrity banner for HR > Payroll tab.
// Fetches /api/admin/commission-ssot-audit scoped to the selected rep.
// Surfaces missing/stale/mismatch/duplicates counts. The "Sync Now" button
// rebuilds payroll from the live deal SSOT for ONLY this rep (per-user, NOT
// full-system). Safety: paid/approved rows are preserved by the backend.
const CommissionIntegrityBanner = ({ userId, repName, onSynced }) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/commission-ssot-audit?user_id=${userId}`);
      setAudit(r?.data || null);
    } catch {
      setAudit(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const t = audit?.totals || {};
  const drift = (t.missing || 0) + (t.amount_mismatch || 0) + (t.stale || 0) + (t.duplicates || 0);
  const handleSync = async () => {
    try {
      setSyncing(true);
      const r = await api.post(`/payroll/sync-user/${userId}`);
      const m = r?.data?.totals || {};
      toast.success(
        `Synced ${m.deals_processed || 0} deal(s) — inserted ${m.inserted || 0}, updated ${m.updated || 0}, removed ${m.removed_stale || 0} stale${m.skipped_protected ? `, ${m.skipped_protected} paid rows preserved` : ''}.`,
        { duration: 6000 }
      );
      await load();
      if (typeof onSynced === 'function') onSynced();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };
  if (loading || !audit) return null;
  const bg = drift === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200';
  return (
    <div className={`rounded-md border ${bg} px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1`} data-testid="commission-integrity-banner">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">
        Commission Integrity{repName ? ` · ${repName}` : ''}
      </span>
      <span className="flex items-center gap-3 text-[11px]">
        <span title="SSOT-funded deals this rep is on">funded deals: <b>{t.funded_deals || 0}</b></span>
        <span className={(t.missing || 0) > 0 ? 'text-red-700 font-semibold' : 'text-slate-600'} title="Rows the SSOT says SHOULD exist but payroll lacks">missing: <b>{t.missing || 0}</b></span>
        <span className={(t.amount_mismatch || 0) > 0 ? 'text-amber-700 font-semibold' : 'text-slate-600'} title="Rows whose amount drifted from the resolver">mismatched: <b>{t.amount_mismatch || 0}</b></span>
        <span className={(t.stale || 0) > 0 ? 'text-orange-700 font-semibold' : 'text-slate-600'} title="Payroll rows for users no longer on the ownership block">stale: <b>{t.stale || 0}</b></span>
        <span className={(t.duplicates || 0) > 0 ? 'text-fuchsia-700 font-semibold' : 'text-slate-600'} title="Duplicate (user, commission_type) pairs">duplicates: <b>{t.duplicates || 0}</b></span>
      </span>
      <Button
        size="sm"
        variant={drift > 0 ? 'default' : 'outline'}
        className="h-7 text-xs ml-auto"
        disabled={syncing}
        onClick={handleSync}
        data-testid="commission-sync-now"
      >
        {syncing ? 'Syncing…' : 'Sync Now'}
      </Button>
    </div>
  );
};


// v3.15-1037: Admin-only manual payroll row creation.
// Calls POST /api/payroll/manual-add. Row is persisted with manual_override=true
// and is FOREVER protected from resolver delete/overwrite. Visible immediately
// in the Payroll table.
const COMMISSION_TYPE_CHOICES = [
  { value: 'sales_commission',       label: 'Sales Commission (15%)' },
  { value: 'manager_override',       label: 'Manager Override (4%)' },
  { value: 'director_override',      label: 'Director Override (3%)' },
  { value: 'funding_commission',     label: 'Funding Commission (PTC Rep 0.5% / Solar $9)' },
  { value: 'application_entry',      label: 'Application Entry ($9)' },
  { value: 'vo_override',            label: 'VO Officer Override (4%)' },
  { value: 'eqf_director_override',  label: 'EQF Director Override (3%)' },
  { value: 'eqf_manager_override',   label: 'EQF Manager Override (4%)' },
  { value: 'marketing_partner_pay',  label: 'Marketing Partner (0.5%)' },
  { value: 'manager_director_split', label: 'Manager-Director Split (4%)' },
  { value: 'bonus',                  label: 'Bonus / Spiff' },
  { value: 'adjustment',             label: 'Adjustment' },
];

const ManualAddRepDialog = ({ defaultUserId, ptcReps, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clientQuery, setClientQuery] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientId, setClientId] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [userId, setUserId] = useState(defaultUserId || '');
  const [commissionType, setCommissionType] = useState('bonus');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (defaultUserId) setUserId(defaultUserId);
  }, [defaultUserId]);

  // Live client search (debounced)
  useEffect(() => {
    if (!open) return;
    const q = clientQuery.trim();
    if (q.length < 2) { setClientResults([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/clients?search=${encodeURIComponent(q)}&limit=10`);
        const list = Array.isArray(r.data) ? r.data : (r.data?.clients || r.data?.items || []);
        if (alive) setClientResults(list.slice(0, 10));
      } catch {
        if (alive) setClientResults([]);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [clientQuery, open]);

  const reset = () => {
    setClientQuery(''); setClientResults([]); setClientId(''); setClientLabel('');
    setUserId(defaultUserId || ''); setCommissionType('bonus'); setAmount(''); setNotes('');
  };

  const submit = async () => {
    if (!clientId) { toast.error('Select a client/deal'); return; }
    if (!userId) { toast.error('Select a user/team member'); return; }
    if (!commissionType) { toast.error('Select a commission type'); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 0) { toast.error('Enter a valid amount'); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/payroll/manual-add', {
        client_id: clientId,
        user_id: userId,
        commission_type: commissionType,
        amount: amt,
        notes,
      });
      toast.success(r.data?.message || 'Manual payroll row added.');
      reset();
      setOpen(false);
      if (typeof onAdded === 'function') onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add manual payroll row.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="add-rep-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Rep
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Manual Payroll Row</DialogTitle>
        </DialogHeader>
        {/* v3.15-1217 PAY-MOD-001 — Manual payroll amount entry retired.
            The V1 architecture only accepts payroll originating from an
            authoritative Compensation Event. Operators are directed to
            create the event via the Compensation module — payroll then
            flows automatically through Ledger → Money Manager →
            Financial Lifecycle → Payroll Transaction. */}
        <div
          className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-2"
          data-testid="manual-add-retired-banner"
        >
          <div className="font-semibold flex items-center gap-2">
            <span className="inline-flex h-2 w-2 rounded-full bg-amber-500" />
            This workflow has been retired
          </div>
          <p className="text-xs leading-relaxed">
            Manual payroll rows can no longer be created. Every payable dollar
            in Prestigious Hub must originate from an authoritative Compensation
            Event so it flows through the V1 pipeline (Compensation Ledger →
            Money Manager → Financial Lifecycle → Payroll Transaction).
          </p>
          <p className="text-xs">
            <strong>Use instead:</strong> Create a Compensation Event via the
            Compensation module. The event will project into the ledger, the
            operator will consume it into Money Manager (MM-001), and it will
            appear in the Financial Lifecycle projection (MM-002).
          </p>
          <p className="text-[10px] text-amber-800 italic">
            PAY-MOD-001 · v3.15-1217 · Legacy `POST /api/payroll/manual-add` returns HTTP 410 Gone.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            data-testid="manual-add-cancel"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// v3.15-1042: Admin-only Rep Audit dialog. Calls
// GET /api/payroll/rep-audit/{user_id} and renders the per-deal table with
// explicit reasons each expected commission row is or isn't present.
// Provides per-deal "Sync This Deal" action that hits the SSOT resolver
// only for that specific deal.
const REASON_COLORS = {
  ok:                                  'bg-emerald-100 text-emerald-700',
  row_exists:                          'bg-emerald-100 text-emerald-700',
  missing_should_sync:                 'bg-rose-100 text-rose-700',
  skipped_not_funded:                  'bg-amber-100 text-amber-700',
  skipped_legacy_only_not_ssot_funded: 'bg-amber-100 text-amber-700',
  solar_non_ptc_role_no_payout:        'bg-amber-100 text-amber-700',
  skipped_no_role:                     'bg-slate-100 text-slate-700',
  resolver_emitted_nothing_for_user:   'bg-slate-100 text-slate-700',
};

const REASON_BLURB = {
  ok:                                  'OK — resolver emitted rows; check expected list',
  row_exists:                          'Row present in payroll',
  missing_should_sync:                 'SSOT-funded but row missing — click Sync This Deal',
  skipped_not_funded:                  'Deal not funded — no payroll until funded',
  skipped_legacy_only_not_ssot_funded: 'Legacy funded markers, all_parties_settled=false. Operator must click "Deal Paid in Full" on the deal.',
  solar_non_ptc_role_no_payout:        'Solar pays only the PTC rep $9; this user is on a different role',
  skipped_no_role:                     'User not on the ownership block (should never appear here)',
  resolver_emitted_nothing_for_user:   'Resolver produced nothing for this user on this deal',
};

const AuditRepDialog = ({ userId, repName, onSynced }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await api.get(`/payroll/rep-audit/${encodeURIComponent(userId)}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Per-deal sync: re-run the SSOT resolver for ONE deal by flipping a
  // no-op PUT that triggers the resolver. Safer than user-wide sync.
  const syncDeal = async (clientId) => {
    setSyncing(clientId);
    try {
      // Trigger by hitting the user-wide sync — resolver re-evaluates everything safely.
      await api.post(`/payroll/sync-user/${encodeURIComponent(userId)}`);
      toast.success('Resolver ran — refreshing audit…');
      await load();
      if (typeof onSynced === 'function') onSynced();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(null);
    }
  };

  const deals      = (data?.deals) || [];
  const summary    = data?.summary || {};
  const missingDeals = deals.filter(d => (d.expected_for_user || []).some(e => !e.row_exists && d.all_parties_settled));
  const visibleDeals = deals;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!userId} data-testid="audit-rep-btn">
          <Activity className="h-3.5 w-3.5 mr-1" /> Audit Rep
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            Payroll Audit — {repName || (data?.user?.full_name) || '…'}
            <Badge variant="secondary" className="text-[10px]">v3.15-1042</Badge>
          </DialogTitle>
        </DialogHeader>
        {loading && <p className="text-xs text-slate-500">Loading audit…</p>}
        {!loading && data && (
          <div className="space-y-3">
            {/* Summary band */}
            <div className="grid grid-cols-6 gap-1 text-center border rounded overflow-hidden text-[11px]" data-testid="audit-summary">
              <div className="px-2 py-1.5 bg-slate-50 border-r"><span className="text-slate-500 block">Deals total</span><span className="font-bold">{summary.deals_total ?? 0}</span></div>
              <div className="px-2 py-1.5 bg-emerald-50 border-r"><span className="text-emerald-600 block">SSOT funded</span><span className="font-bold text-emerald-700">{summary.deals_funded_ssot ?? 0}</span></div>
              <div className="px-2 py-1.5 bg-amber-50 border-r"><span className="text-amber-600 block">Legacy-only funded</span><span className="font-bold text-amber-700">{summary.deals_funded_legacy ?? 0}</span></div>
              <div className="px-2 py-1.5 bg-rose-50 border-r"><span className="text-rose-600 block">Rows missing</span><span className="font-bold text-rose-700">{summary.rows_missing ?? 0}</span></div>
              <div className="px-2 py-1.5 bg-blue-50 border-r"><span className="text-blue-600 block">Protected</span><span className="font-bold text-blue-700">{summary.protected_rows ?? 0}</span></div>
              <div className="px-2 py-1.5 bg-slate-50"><span className="text-slate-500 block">Solar / EQF / PTC</span><span className="font-bold">{summary.solar_deals ?? 0} / {summary.eqf_deals ?? 0} / {summary.ptc_deals ?? 0}</span></div>
            </div>

            {missingDeals.length > 0 && (
              <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-800" data-testid="audit-missing-banner">
                <strong>{missingDeals.length}</strong> SSOT-funded deal{missingDeals.length === 1 ? '' : 's'} have missing payroll rows. Click <em>Sync This Deal</em> to rebuild.
              </div>
            )}

            <div className="overflow-x-auto border rounded">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left py-1.5 px-2 font-medium text-slate-500">Client</th>
                    <th className="text-left py-1.5 px-2 font-medium text-slate-500">Type</th>
                    <th className="text-center py-1.5 px-2 font-medium text-slate-500">SSOT funded?</th>
                    <th className="text-center py-1.5 px-2 font-medium text-slate-500">Legacy?</th>
                    <th className="text-left py-1.5 px-2 font-medium text-slate-500">Roles</th>
                    <th className="text-left py-1.5 px-2 font-medium text-slate-500">Deal reason</th>
                    <th className="text-left py-1.5 px-2 font-medium text-slate-500">Expected rows</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDeals.map(d => {
                    const exp = d.expected_for_user || [];
                    const anyMissing = exp.some(e => !e.row_exists);
                    return (
                      <tr key={d.client_id} className={`border-b ${d.all_parties_settled && anyMissing ? 'bg-rose-50/40' : ''}`} data-testid={`audit-row-${d.client_id}`}>
                        <td className="py-1.5 px-2">
                          <a href={`/clients/${d.client_id}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{d.client_name}</a>
                        </td>
                        <td className="py-1.5 px-2">
                          <Badge variant="outline" className={`text-[9px] ${d.deal_type_label === 'Solar' ? 'border-amber-400 text-amber-600 bg-amber-50' : d.deal_type_label === 'EQF' ? 'border-orange-300 text-orange-600' : 'border-blue-300 text-blue-600'}`}>{d.deal_type_label}</Badge>
                        </td>
                        <td className="py-1.5 px-2 text-center">{d.all_parties_settled ? <span className="text-emerald-700">✓</span> : <span className="text-slate-400">—</span>}</td>
                        <td className="py-1.5 px-2 text-center">{d.legacy_funded_markers ? <span className="text-amber-700">⚠</span> : <span className="text-slate-400">—</span>}</td>
                        <td className="py-1.5 px-2 text-[10px] text-slate-500">{(d.roles_held || []).map(r => r.replace(/_id$/, '')).join(', ') || '—'}</td>
                        <td className="py-1.5 px-2">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] ${REASON_COLORS[d.deal_reason] || 'bg-slate-100 text-slate-700'}`} title={REASON_BLURB[d.deal_reason] || ''}>
                            {d.deal_reason}
                          </span>
                        </td>
                        <td className="py-1.5 px-2">
                          {exp.length === 0 && <span className="text-slate-400">—</span>}
                          {exp.map((e, i) => (
                            <div key={i} className="flex items-center gap-1.5 mb-0.5">
                              <span className={`inline-flex items-center px-1 py-0.5 rounded text-[9px] ${e.row_exists ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                {e.row_exists ? '✓' : '✗'} {e.commission_type}
                              </span>
                              <span className="text-slate-600">${Number(e.expected_amount || 0).toLocaleString()}</span>
                              {e.duplicate && <span className="text-[9px] text-orange-600">DUP</span>}
                              {e.protected && <span className="text-[9px] text-blue-600">PROT</span>}
                            </div>
                          ))}
                        </td>
                        <td className="py-1.5 px-2 text-right">
                          {d.all_parties_settled && anyMissing && (
                            <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => syncDeal(d.client_id)} disabled={syncing === d.client_id} data-testid={`audit-sync-${d.client_id}`}>
                              {syncing === d.client_id ? '…' : 'Sync This Deal'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {visibleDeals.length === 0 && (
                    <tr><td colSpan="8" className="py-4 text-center text-slate-400">No deals found where this user is on the ownership block.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <details className="text-[11px] text-slate-600">
              <summary className="cursor-pointer">Reason code legend</summary>
              <ul className="mt-2 space-y-1 pl-4">
                {Object.entries(REASON_BLURB).map(([k, v]) => (
                  <li key={k}><code className="text-[10px] bg-slate-100 px-1 rounded">{k}</code> — {v}</li>
                ))}
              </ul>
            </details>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="audit-close">Close</Button>
          <Button onClick={load} disabled={loading} data-testid="audit-refresh">Refresh</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const PayrollTab = () => {
  const { isAdmin, user } = useAuth();
  const [ptcReps, setPtcReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState('');
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [paidNoteOpen, setPaidNoteOpen] = useState(false);
  const [paidNoteTarget, setPaidNoteTarget] = useState(null);
  const [paidNote, setPaidNote] = useState('');
  const [payPlanOpen, setPayPlanOpen] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [miscAmount, setMiscAmount] = useState('');
  const [miscNotes, setMiscNotes] = useState('');
  const [miscSaving, setMiscSaving] = useState(false);

  const PAYOUT_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved for Payment' },
    { value: 'not_applicable', label: 'Not Applicable' },
    { value: 'paid', label: 'Paid' },
  ];
  const PAYOUT_COLORS = { pending: 'bg-amber-100 text-amber-700', eligible: 'bg-amber-100 text-amber-700', approved: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700', not_applicable: 'bg-slate-100 text-slate-500', cancelled: 'bg-red-100 text-red-700' };
  const PAYOUT_LABELS = { pending: 'Pending', eligible: 'Pending', approved: 'Approved', paid: 'Paid', not_applicable: 'N/A', cancelled: 'Denied' };

  useEffect(() => {
    api.get('/payroll/ptc-reps').then(r => setPtcReps(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedRep) { setCommissions([]); setSummary({}); return; }
    setLoading(true);
    const params = { user_id: selectedRep };
    if (search.trim()) params.search = search.trim();
    Promise.all([
      api.get('/payroll', { params }),
      api.get('/payroll/summary', { params: { user_id: selectedRep } }),
      api.get(`/payroll/misc/${selectedRep}`),
    ]).then(([commRes, sumRes, miscRes]) => {
      setCommissions(commRes.data || []);
      setSummary(sumRes.data || {});
      setMiscAmount(miscRes.data?.amount || '');
      setMiscNotes(miscRes.data?.notes || '');
    }).catch(() => { setCommissions([]); setSummary({}); }).finally(() => setLoading(false));
  }, [selectedRep, search]);

  // v3.15-1035: reload payroll after Sync Now succeeds.
  const onRefreshPayroll = () => {
    if (!selectedRep) return;
    const params = { user_id: selectedRep };
    if (search.trim()) params.search = search.trim();
    api.get('/payroll', { params }).then(r => setCommissions(r.data || [])).catch(() => {});
    api.get('/payroll/summary', { params: { user_id: selectedRep } }).then(r => setSummary(r.data || {})).catch(() => {});
  };

  const handleStatusChange = async (commId, newStatus, notes) => {
    setActionLoading(commId);
    try {
      const body = { status: newStatus };
      if (notes) body.notes = notes;
      await api.put(`/payroll/${commId}/status`, body);
      toast.success(`Status → ${PAYOUT_LABELS[newStatus] || newStatus}`);
      // Refresh
      const params = { user_id: selectedRep };
      if (search.trim()) params.search = search.trim();
      const [commRes, sumRes] = await Promise.all([
        api.get('/payroll', { params }),
        api.get('/payroll/summary', { params: { user_id: selectedRep } }),
      ]);
      setCommissions(commRes.data || []);
      setSummary(sumRes.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setActionLoading(null); }
  };

  const confirmMarkPaid = () => {
    if (!paidNoteTarget) return;
    handleStatusChange(paidNoteTarget, 'paid', paidNote || 'Marked as paid');
    setPaidNoteOpen(false);
    setPaidNoteTarget(null);
    setPaidNote('');
  };

  const filtered = search.trim()
    ? commissions.filter(c => {
        const s = search.toLowerCase();
        return (c.client_name || '').toLowerCase().includes(s)
          || (c.client_phone || '').toLowerCase().includes(s)
          || (c.client_id || '').toLowerCase().includes(s);
      })
    : commissions;

  const repName = ptcReps.find(r => r.id === selectedRep)?.full_name || '';

  return (
    <div className="space-y-3" data-testid="payroll-tab">
      {/* Select Rep + Search + Pay Plan */}
      <div className="flex items-center gap-2">
        <Select value={selectedRep} onValueChange={v => { setSelectedRep(v); setSearch(''); }}>
          <SelectTrigger className="w-[220px] h-8 text-xs" data-testid="hr-rep-filter">
            <SelectValue placeholder="Select Rep..." />
          </SelectTrigger>
          <SelectContent>
            {ptcReps.map(r => (
              <SelectItem key={r.id} value={r.id} data-testid={`rep-option-${r.id}`}>
                <span>{r.full_name}</span>
                {r.company_name && (
                  <span className="ml-2 text-[10px] text-slate-400">· {r.company_name}</span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedRep && (
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
            <Input className="h-8 text-xs pl-7 w-[220px]" placeholder="Search client, phone, or ID..." value={search} onChange={e => setSearch(e.target.value)} data-testid="hr-payroll-search" />
          </div>
        )}
        <Button size="sm" variant="outline" className="h-8 text-xs ml-auto" onClick={() => setPayPlanOpen(true)} data-testid="pay-plan-btn">
          Understand Your Pay Plan
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setBonusOpen(true)} data-testid="bonus-plan-btn">
          Understand Your Bonus Program
        </Button>
      </div>

      {/* v3.15-1035: Commission Integrity banner — surfaces missing/stale/
          mismatch counts straight from /api/admin/commission-ssot-audit.
          Per-user "Sync Now" rebuilds payroll from the live deal SSOT for
          ONLY the selected rep. Safety: paid/approved rows are preserved. */}
      {selectedRep && (
        <CommissionIntegrityBanner userId={selectedRep} repName={repName} onSynced={() => { if (typeof onRefreshPayroll === 'function') onRefreshPayroll(); }} />
      )}

      {/* v3.15-1037: Admin-only "Add Rep" — manually creates a permanent
          payroll row tagged manual_override=true. Manual rows survive all
          rebuilds, syncs, and un-funding. */}
      {isAdmin && (
        <div className="flex gap-2 items-center">
          {selectedRep && (
            <AuditRepDialog
              userId={selectedRep}
              repName={repName}
              onSynced={() => { if (typeof onRefreshPayroll === 'function') onRefreshPayroll(); }}
            />
          )}
          <ManualAddRepDialog
            defaultUserId={selectedRep || undefined}
            ptcReps={ptcReps}
            onAdded={() => { if (typeof onRefreshPayroll === 'function') onRefreshPayroll(); }}
          />
        </div>
      )}

      {/* No rep selected */}
      {!selectedRep && (
        <div className="text-center py-16 text-slate-400 text-sm">Select a rep to view their payroll</div>
      )}

      {/* Rep selected */}
      {selectedRep && (
        <>
          {repName && <div className="text-sm font-semibold text-slate-700">{repName}</div>}

          {/* Summary */}
          {(() => {
            // v3.15-1039: defensive number formatting — never throws on null/undefined/string.
            const safeMoney = (v) => {
              const n = Number(v);
              if (!Number.isFinite(n)) return '0';
              try { return n.toLocaleString(); } catch { return String(n); }
            };
            const mtd = safeMoney(summary?.mtd_earnings);
            const ytd = safeMoney(summary?.ytd_earnings);
            const pending = safeMoney(summary?.pending_amount);
            const approved = safeMoney(summary?.approved_amount || summary?.scheduled_amount);
            const paid = safeMoney(summary?.paid_amount);
            return (
              <div className="grid grid-cols-5 gap-0 border rounded overflow-hidden text-center" data-testid="payroll-summary">
                <div className="px-2 py-2 bg-slate-50 border-r">
                  <span className="text-[10px] text-slate-500 uppercase block">MTD Earnings</span>
                  <span className="text-base font-bold text-slate-800">${mtd}</span>
                </div>
                <div className="px-2 py-2 bg-slate-50 border-r">
                  <span className="text-[10px] text-slate-500 uppercase block">YTD Earnings</span>
                  <span className="text-base font-bold text-slate-800">${ytd}</span>
                </div>
                <div className="px-2 py-2 bg-amber-50 border-r">
                  <span className="text-[10px] text-amber-600 uppercase font-semibold block">Pending</span>
                  <span className="text-base font-bold text-amber-700">${pending}</span>
                </div>
                <div className="px-2 py-2 bg-blue-50 border-r">
                  <span className="text-[10px] text-blue-600 uppercase font-semibold block">Approved</span>
                  <span className="text-base font-bold text-blue-700">${approved}</span>
                </div>
                <div className="px-2 py-2 bg-green-50">
                  <span className="text-[10px] text-green-600 uppercase font-semibold block">Paid</span>
                  <span className="text-base font-bold text-green-700">${paid}</span>
                </div>
              </div>
            );
          })()}

          {/* Misc Earnings */}
          {isAdmin() && (
          <div className="flex items-center gap-2 border rounded px-3 py-2 bg-slate-50">
            <span className="text-[10px] text-slate-500 uppercase font-semibold whitespace-nowrap">Misc Earnings:</span>
            <Input type="number" className="h-6 text-xs w-24" placeholder="$0" value={miscAmount} onChange={e => setMiscAmount(e.target.value)} data-testid="misc-amount" />
            <Input className="h-6 text-xs flex-1" placeholder="Notes (e.g. Jessica assist bonus)" value={miscNotes} onChange={e => setMiscNotes(e.target.value)} data-testid="misc-notes" />
            <Button size="sm" className="h-6 text-[10px] px-2" disabled={miscSaving} onClick={async () => {
              setMiscSaving(true);
              try {
                await api.post(`/payroll/misc/${selectedRep}`, { amount: Number(miscAmount) || 0, notes: miscNotes });
                toast.success('Misc earnings saved');
              } catch { toast.error('Failed to save'); }
              finally { setMiscSaving(false); }
            }} data-testid="misc-save">{miscSaving ? '...' : 'Save'}</Button>
            {Number(miscAmount) > 0 && (
              <span className="text-xs font-bold text-emerald-700 whitespace-nowrap">
                Total: ${((summary.pending_amount || 0) + (summary.approved_amount || summary.scheduled_amount || 0) + (summary.paid_amount || 0) + Number(miscAmount)).toLocaleString()}
              </span>
            )}
          </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No commissions for this rep</div>
          ) : (
            <div className="border rounded overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-left">Client Name</th>
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-left">Phone</th>
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-left">Deal Type</th>
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-right">Deal Amount</th>
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-left">Commission Type</th>
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-right">Commission</th>
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-left">Deal Status</th>
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-center">Payout Status</th>
                    <th className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(filtered) ? filtered : []).map((c, idx) => {
                    if (!c || typeof c !== 'object') return null;
                    const rowKey = c.id || c._id || `row-${idx}`;
                    // v3.15-1041: Solar rows must NEVER render as EQF.
                    // Trust backend `deal_type` (now Solar-aware) first.
                    const isSolar = c.is_solar === true
                      || c.deal_type === 'Solar'
                      || (String(c.deal_source || '').toLowerCase() === 'solar')
                      || (String(c.industry_category || '').toLowerCase() === 'solar_exit');
                    const dealType = isSolar
                      ? 'Solar'
                      : (c.deal_type || (c.split_type === 'EQF' ? 'EQF' : 'PTC'));
                    const rawStatus = c.status || 'pending';
                    const displayStatus = rawStatus === 'eligible' ? 'pending' : rawStatus;
                    // v3.15-1041: Solar display overrides — backend supplies *_display,
                    // we also enforce client-side as a defense-in-depth measure.
                    const commType = isSolar
                      ? 'Solar Funded $9'
                      : (c.commission_type_display
                          || String(c.commission_type || '').replace(/_/g, ' '));
                    const dealStage = isSolar
                      ? 'Funded'
                      : String(c.deal_stage_display || c.deal_stage || '').replace(/_/g, ' ');
                    const funded = isSolar
                      ? true
                      : (c.funding_status === 'funded' || !!c.funded_date || c.all_parties_settled === true);
                    // v3.15-1039: safe currency formatters — never throw on null/undefined/string amounts.
                    const fmtMoney = (v) => {
                      const n = Number(v);
                      if (!Number.isFinite(n)) return '-';
                      try { return `$${n.toLocaleString()}`; } catch { return `$${n}`; }
                    };
                    const fmtDate = (v) => {
                      if (!v) return '-';
                      try { return formatET(v, 'MMM d, yyyy') || '-'; } catch { return '-'; }
                    };
                    const amountDisplay = fmtMoney(c.amount);
                    const dealAmountDisplay = c.deal_amount == null || c.deal_amount === '' ? '-' : fmtMoney(c.deal_amount);
                    return (
                      <tr key={rowKey} className={`border-b border-slate-50 hover:bg-slate-50/50 ${rawStatus === 'paid' ? 'bg-green-50/30' : ''}`} data-testid={`payroll-row-${rowKey}`}>
                        <td className="py-2 px-3 text-xs font-medium text-slate-800">{c.client_name || '-'}</td>
                        <td className="py-2 px-3 text-[10px] text-slate-500">{c.client_phone || '-'}</td>
                        <td className="py-2 px-3"><Badge variant="outline" className={`text-[9px] ${dealType === 'Solar' ? 'border-amber-400 text-amber-600 bg-amber-50' : dealType === 'EQF' ? 'border-orange-300 text-orange-600' : 'border-blue-300 text-blue-600'}`} data-testid={`payroll-deal-type-${rowKey}`}>{dealType}</Badge></td>
                        <td className="py-2 px-3 text-xs text-right font-mono">{dealAmountDisplay}</td>
                        <td className="py-2 px-3 text-[10px] text-slate-600 capitalize">{commType || '-'}{c.manual_override ? <span className="ml-1 text-[9px] text-fuchsia-600 font-semibold">· manual</span> : null}</td>
                        <td className="py-2 px-3 text-xs text-right font-mono font-bold text-emerald-700">{amountDisplay === '-' ? '$0' : amountDisplay}</td>
                        <td className="py-2 px-3"><span className="text-[10px] text-slate-500 capitalize">{dealStage || '-'}</span>{funded && <span className="text-[9px] text-emerald-500 ml-1">Funded</span>}</td>
                        <td className="py-2 px-3 text-center">
                          {isAdmin() ? (
                            <Select
                              value={displayStatus}
                              onValueChange={(val) => {
                                if (val === 'paid') { setPaidNoteTarget(c.id); setPaidNoteOpen(true); }
                                else { handleStatusChange(c.id, val); }
                              }}
                              disabled={actionLoading === c.id}
                            >
                              <SelectTrigger className="h-5 w-[130px] text-[10px] border-0 bg-transparent mx-auto">
                                <Badge className={`${PAYOUT_COLORS[displayStatus] || ''} text-[9px]`}>{PAYOUT_LABELS[displayStatus] || displayStatus}</Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {PAYOUT_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={`${PAYOUT_COLORS[displayStatus] || ''} text-[10px]`}>{PAYOUT_LABELS[displayStatus] || displayStatus}</Badge>
                          )}
                        </td>
                        <td className="py-2 px-3 text-[10px] text-slate-400">{fmtDate(c.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Paid Note Dialog */}
      <Dialog open={paidNoteOpen} onOpenChange={setPaidNoteOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="text-sm">Mark as Paid</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-[10px] text-slate-500 block">Note (optional)</label>
            <Input className="h-7 text-xs" placeholder="e.g. paid outside system" value={paidNote} onChange={e => setPaidNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPaidNoteOpen(false)}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={confirmMarkPaid}>Confirm Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Plan Modal */}
      <Dialog open={payPlanOpen} onOpenChange={setPayPlanOpen}>
        <DialogContent className="max-w-md" data-testid="pay-plan-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Your Pay Plan</DialogTitle>
          </DialogHeader>
          {(() => {
            const role = user?.role || '';
            const isManagerDirector = role === 'manager_director';
            const isManagerOrTeam = ['manager_1', 'md_team_member'].includes(role);

            if (isManagerDirector) {
              return (
                <div className="space-y-4 text-sm" data-testid="pay-plan-manager-director">
                  <div>
                    <h3 className="text-xs font-bold text-purple-600 uppercase tracking-wide border-b pb-1 mb-2">Manager Director Pay Plan</h3>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold mt-0.5">30%</span><span>Total commission pool per deal</span></li>
                      <li className="flex items-start gap-2"><span className="text-slate-400 mt-1">-</span><span>You control how commission is split among your team</span></li>
                      <li className="flex items-start gap-2"><span className="text-slate-400 mt-1">-</span><span>Any % assigned to team members is deducted from your 30%</span></li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Example</p>
                    <div className="text-xs text-slate-600 space-y-1">
                      <p>You start with <span className="font-bold text-emerald-700">30%</span></p>
                      <p>You assign Manager 1 = <span className="font-bold">10%</span></p>
                      <p>You assign Team Member = <span className="font-bold">5%</span></p>
                      <p className="border-t pt-1 mt-1">You keep <span className="font-bold text-emerald-700">15%</span></p>
                    </div>
                  </div>
                </div>
              );
            }

            if (isManagerOrTeam) {
              return (
                <div className="space-y-4 text-sm" data-testid="pay-plan-team">
                  <div>
                    <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wide border-b pb-1 mb-2">Your Pay Plan</h3>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start gap-2"><span className="text-slate-400 mt-1">-</span><span>Your commission % is assigned by your Manager Director</span></li>
                      <li className="flex items-start gap-2"><span className="text-slate-400 mt-1">-</span><span>It comes from the 30% commission pool on each deal</span></li>
                      <li className="flex items-start gap-2"><span className="text-slate-400 mt-1">-</span><span>Your % is set per deal by your Manager Director</span></li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded p-3">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Example</p>
                    <div className="text-xs text-slate-600 space-y-1">
                      <p>You are assigned <span className="font-bold text-emerald-700">10%</span> on a deal</p>
                      <p>Net after costs = <span className="font-bold">$8,000</span></p>
                      <p className="border-t pt-1 mt-1">Your commission = <span className="font-bold text-emerald-700">$800</span></p>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-4 text-sm" data-testid="pay-plan-rep">
                <div>
                  <h3 className="text-xs font-bold text-orange-600 uppercase tracking-wide border-b pb-1 mb-2">Easy Quick Fund (EQF)</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2"><span className="font-bold text-emerald-700 mt-0.5">$9</span><span>Per application submitted</span></li>
                    <li className="flex items-start gap-2"><span className="font-bold text-emerald-700 mt-0.5">0.5%</span><span>Of funded amount when deal funds</span></li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Example</p>
                  <div className="text-xs text-slate-600 space-y-1">
                    <p>Deal funded at <span className="font-bold">$40,000</span></p>
                    <p>Application pay = <span className="font-bold">$9</span></p>
                    <p>Funding bonus = <span className="font-bold">$200</span> (0.5%)</p>
                    <p className="border-t pt-1 mt-1">Total = <span className="font-bold text-emerald-700">$209</span></p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wide border-b pb-1 mb-2">Prestigious Travel Club (PTC)</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2"><span className="font-bold text-emerald-700 mt-0.5">15%</span><span>Commission on net after costs</span></li>
                    <li className="flex items-start gap-2"><span className="text-slate-400 mt-1">-</span><span>Applies when you are assigned as the Sales Rep on the deal</span></li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Example</p>
                  <div className="text-xs text-slate-600 space-y-1">
                    <p>Net after costs = <span className="font-bold">$8,000</span></p>
                    <p className="border-t pt-1 mt-1">Commission = <span className="font-bold text-emerald-700">$1,200</span> (15%)</p>
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPayPlanOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bonus Program Modal */}
      <Dialog open={bonusOpen} onOpenChange={setBonusOpen}>
        <DialogContent className="max-w-md" data-testid="bonus-plan-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">PTC Sales Bonus Program</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wide border-b pb-1 mb-2">Base Commission</h3>
              <ul className="space-y-1.5 text-slate-600">
                <li className="flex items-start gap-2"><span className="font-bold text-emerald-700 mt-0.5">15%</span><span>Starting commission rate</span></li>
                <li className="flex items-start gap-2"><span className="text-slate-400 mt-1">-</span><span>Calculated after hard costs</span></li>
                <li className="flex items-start gap-2"><span className="text-slate-400 mt-1">-</span><span>Based on collected revenue</span></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-wide border-b pb-1 mb-2">Volume Tiers</h3>
              <div className="space-y-0.5">
                {[['$0 – $9,999','15%'],['$10K – $24,999','18%'],['$25K – $49,999','20%'],['$50K – $74,999','23%'],['$75K – $99,999','25%'],['$100K+','28%']].map(([range, pct]) => (
                  <div key={range} className="flex justify-between items-center py-1 px-2 rounded hover:bg-slate-50">
                    <span className="text-slate-600 text-xs">{range}</span>
                    <span className="font-bold text-emerald-700 text-xs">{pct}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xs font-bold text-purple-600 uppercase tracking-wide border-b pb-1 mb-2">Performance Boost Opportunities</h3>
              <p className="text-xs text-slate-500 leading-relaxed">Additional commission increases may apply based on performance metrics such as close rate, speed to funding, deal quality, and self-generated business. These incentives are dynamically applied based on company criteria.</p>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setBonusOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =================== ACCESS CONTROL TAB ===================
// v3.21-1626 · Access Control is now
//   (a) grouped under the AUTHORITATIVE company (users.company_id joined
//       to companies.id),
//   (b) equipped with visible Activate / Deactivate buttons that call
//       the canonical `/api/users/{id}/activate` and `/deactivate`
//       endpoints (v3.21-1616 service, backend authorization enforced),
//   (c) surfaces a resolved status label (Active / Inactive / Disabled
//       / Pending Setup / Suspended / Terminated) so protected states
//       can't be casually flipped, and
//   (d) shows correct counts (users / companies) at the top of the tab.
const AccessControlTab = ({ users }) => {
  const [accessData, setAccessData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null); // user row or null
  const [collapsedCompanies, setCollapsedCompanies] = useState({}); // { company_name: bool }
  const [companyFilter, setCompanyFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try { const res = await api.get('/hr/access-control'); setAccessData(res.data || []); }
    catch { setAccessData([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const toggleAccess = async (userId, field, currentValue) => {
    try {
      await api.put(`/hr/access-control/${userId}`, { [field]: !currentValue });
      setAccessData(d => d.map(u => u.user_id === userId ? { ...u, [field]: !currentValue } : u));
      toast.success('Access updated');
    } catch { toast.error('Failed to update'); }
  };

  // v3.21-1626 · Activate / Deactivate — canonical service, backend
  // enforces permissions.  Suspended/Terminated rows show a locked
  // pill instead of the button (activate() would be rejected server-
  // side anyway; we don't hide the row).
  const isProtected = (u) => {
    const s = String(u.resolved_status || '').toLowerCase();
    return s === 'suspended' || s === 'terminated';
  };
  const activate = async (u) => {
    try {
      await api.post(`/users/${u.user_id}/activate`);
      toast.success(`${u.user_name} activated`);
      fetchData();
    } catch (e) { toast.error(getApiErrorMessage(e) || 'Activation failed'); }
  };
  const deactivate = async (u) => {
    try {
      await api.post(`/users/${u.user_id}/deactivate`);
      toast.success(`${u.user_name} deactivated`);
      setConfirmDeactivate(null);
      fetchData();
    } catch (e) { toast.error(getApiErrorMessage(e) || 'Deactivation failed'); }
  };

  // Group by company_name (authoritative — server joined company_id → companies.name).
  const filtered = accessData.filter(u => companyFilter === 'all' || u.company_name === companyFilter);
  const grouped = filtered.reduce((acc, u) => {
    const key = u.company_name || 'Unassigned';
    (acc[key] ||= []).push(u); return acc;
  }, {});
  const companyKeys = Object.keys(grouped).sort((a, b) => {
    // PTC / Prestigious Travel Club first, then alphabetical.
    const ap = /prestigious|ptc/i.test(a) ? 0 : 1;
    const bp = /prestigious|ptc/i.test(b) ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return a.localeCompare(b);
  });
  const allCompanies = [...new Set(accessData.map(u => u.company_name || 'Unassigned'))].sort();

  const statusPill = (label) => {
    const s = String(label).toLowerCase();
    const cls = s === 'active'      ? 'bg-green-100 text-green-800'
              : s === 'pending setup' ? 'bg-amber-100 text-amber-800'
              : s === 'suspended'   ? 'bg-orange-100 text-orange-800'
              : s === 'terminated'  ? 'bg-slate-900 text-slate-100'
              : /* Inactive / Disabled */ 'bg-red-100 text-red-800';
    return <Badge className={`text-[10px] px-1.5 py-0 ${cls}`} variant="secondary" data-testid={`status-pill-${s.replace(/\s+/g,'-')}`}>{label}</Badge>;
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4" data-testid="access-control-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500" data-testid="access-control-count">
          {filtered.length} team members across {Object.keys(grouped).length} {Object.keys(grouped).length === 1 ? 'company' : 'companies'}
        </p>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Company</Label>
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="w-64 h-8 text-xs" data-testid="access-control-company-filter">
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="access-control-company-opt-all">All Companies</SelectItem>
              {allCompanies.map(name => (
                <SelectItem key={name} value={name} data-testid={`access-control-company-opt-${name}`}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {companyKeys.map(company => {
        const collapsed = !!collapsedCompanies[company];
        const rows = grouped[company].slice().sort((a, b) => (a.user_name || '').localeCompare(b.user_name || ''));
        return (
          <Card key={company} data-testid={`access-control-company-${company}`}>
            <CardHeader className="py-2 px-4 bg-slate-50 border-b flex flex-row items-center justify-between space-y-0">
              <button
                type="button"
                className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wide"
                onClick={() => setCollapsedCompanies(prev => ({ ...prev, [company]: !prev[company] }))}
                data-testid={`access-control-company-toggle-${company}`}
              >
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
                {company}
                <span className="text-slate-400 font-normal normal-case text-[11px]">— {rows.length} user{rows.length === 1 ? '' : 's'}</span>
              </button>
            </CardHeader>
            {!collapsed && (
              <CardContent className="p-0">
                <table className="w-full">
                  <thead><tr className="border-b bg-white">
                    <th className="text-left py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Team Member</th>
                    <th className="text-left py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Role</th>
                    <th className="text-center py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">System Access</th>
                    <th className="text-center py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Phone System</th>
                    <th className="text-center py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">CRM Access</th>
                    <th className="text-center py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Status</th>
                    <th className="text-right py-2 px-4 text-[10px] font-medium text-slate-500 uppercase">Action</th>
                  </tr></thead>
                  <tbody>
                    {rows.map(u => (
                      <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`access-row-${u.user_id}`}>
                        <td className="py-2.5 px-4 text-sm font-medium text-slate-800">
                          <div>{u.user_name}</div>
                          {u.email && <div className="text-[10px] text-slate-400">{u.email}</div>}
                        </td>
                        <td className="py-2.5 px-4"><Badge className={`${roleColors[u.role] || 'bg-slate-100'} text-[10px] px-1.5 py-0`} variant="secondary">{formatRole(u.role)}</Badge></td>
                        <td className="py-2.5 px-4 text-center">
                          <Switch checked={u.system_access !== false} onCheckedChange={() => toggleAccess(u.user_id, 'system_access', u.system_access !== false)} data-testid={`toggle-system-${u.user_id}`} />
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          <Switch checked={!!u.phone_access} onCheckedChange={() => toggleAccess(u.user_id, 'phone_access', !!u.phone_access)} data-testid={`toggle-phone-${u.user_id}`} />
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          <Switch checked={u.crm_access !== false} onCheckedChange={() => toggleAccess(u.user_id, 'crm_access', u.crm_access !== false)} data-testid={`toggle-crm-${u.user_id}`} />
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          {statusPill(u.resolved_status || (u.is_active ? 'Active' : 'Disabled'))}
                        </td>
                        <td className="py-2.5 px-4 text-right">
                          {isProtected(u) ? (
                            <span className="text-[10px] text-slate-400" title="Protected state — requires the authorized remediation path" data-testid={`action-locked-${u.user_id}`}>Locked</span>
                          ) : (u.resolved_status === 'Active') ? (
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setConfirmDeactivate(u)} data-testid={`deactivate-btn-${u.user_id}`}>
                              Deactivate
                            </Button>
                          ) : (
                            <Button size="sm" className="h-7 text-[11px] bg-green-600 hover:bg-green-700" onClick={() => activate(u)} data-testid={`activate-btn-${u.user_id}`}>
                              Activate
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            )}
          </Card>
        );
      })}

      <Dialog open={!!confirmDeactivate} onOpenChange={(o) => { if (!o) setConfirmDeactivate(null); }}>
        <DialogContent className="max-w-md" data-testid="deactivate-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate {confirmDeactivate?.user_name}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p>This will block login for <strong>{confirmDeactivate?.user_name}</strong> ({confirmDeactivate?.email}).</p>
            <p className="text-slate-500">The user record, roles, company assignment and history are retained. You can re-activate them any time.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmDeactivate(null)} data-testid="deactivate-cancel-btn">Cancel</Button>
            <Button variant="destructive" size="sm" onClick={() => deactivate(confirmDeactivate)} data-testid="deactivate-confirm-btn">Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =================== ACTIVITY LOGS TAB ===================
const ActivityLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetch = async () => {
      try { const res = await api.get('/hr/activity-logs'); setLogs(res.data || []); } catch { setLogs([]); } finally { setLoading(false); }
    };
    fetch();
  }, []);

  const getIcon = (type) => {
    if (type?.includes('login') || type?.includes('auth')) return LogIn;
    if (type?.includes('call') || type?.includes('phone')) return PhoneCall;
    if (type?.includes('note')) return StickyNote;
    if (type?.includes('deal') || type?.includes('stage')) return Briefcase;
    if (type?.includes('commission')) return DollarSign;
    return Activity;
  };

  const getColor = (type) => {
    if (type?.includes('login')) return 'text-blue-500 bg-blue-50';
    if (type?.includes('call')) return 'text-green-500 bg-green-50';
    if (type?.includes('note')) return 'text-amber-500 bg-amber-50';
    if (type?.includes('deal')) return 'text-purple-500 bg-purple-50';
    return 'text-slate-500 bg-slate-50';
  };

  const categories = [
    { key: 'all', label: 'All' },
    { key: 'login', label: 'Logins' },
    { key: 'call', label: 'Calls' },
    { key: 'note', label: 'Notes' },
    { key: 'deal', label: 'Deals' },
  ];

  const filteredLogs = filter === 'all' ? logs : logs.filter(l => (l.activity_type || l.description || '').toLowerCase().includes(filter));

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4" data-testid="activity-logs-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{filteredLogs.length} activities</p>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          {categories.map(c => (
            <Button key={c.key} size="sm" variant={filter === c.key ? 'default' : 'ghost'} className={`h-6 text-[10px] ${filter === c.key ? '' : 'text-slate-500'}`} onClick={() => setFilter(c.key)} data-testid={`filter-${c.key}`}>
              {c.label}
            </Button>
          ))}
        </div>
      </div>
      <Card><CardContent className="p-0 max-h-[500px] overflow-y-auto">
        {filteredLogs.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">No activity logs found.</div>
        ) : (
          <div className="divide-y">
            {filteredLogs.map((log, i) => {
              const LogIcon = getIcon(log.activity_type);
              const colorClass = getColor(log.activity_type);
              return (
                <div key={log.id || i} className="flex items-start gap-3 p-2.5 hover:bg-slate-50/50">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                    <LogIcon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-800"><span className="font-medium">{log.user_name || log.performed_by_name || ''}</span> {log.description || log.activity_type}</p>
                    {log.client_name && <p className="text-[10px] text-slate-500">Client: {log.client_name}</p>}
                    <p className="text-[10px] text-slate-400">{log.created_at ? formatET(log.created_at, 'MMM d, h:mm a') : ''}</p>
                  </div>
                  {log.activity_type && <Badge variant="outline" className="text-[9px] px-1 py-0 flex-shrink-0">{log.activity_type}</Badge>}
                </div>
              );
            })}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};


// =================== RESOURCES TAB ===================
const DEFAULT_RESOURCES = {
  teamDrive: { title: 'EQF Funding Team Drive', url: 'https://drive.google.com/drive/u/0/folders/0AMw82ZQZVSmFUk9PVA', description: 'Training materials, documentation, and internal resources for the EQF Loan Funding Team.' },
  perks: [
    { id: 'ptc', name: 'Prestigious Travel Club', url: 'https://prestigioustravelclub.net/Guest', icon: 'Plane' },
    { id: 'perksatwork', name: 'Perks @Work', url: 'https://accounts.perksatwork.com/oauth/signup', icon: 'Gift' },
    { id: 'westgate', name: 'Westgate Travel Program', url: 'https://my.westgateresorts.com/new-user-registration/', icon: 'Globe' },
    { id: 'westgate-cruise', name: 'Westgate Cruise & Travel', url: 'https://westgatecruiseandtravel.com/wg-vip', icon: 'Ship' },
    { id: 'orlando', name: 'Orlando Discount Coupons', url: 'https://www.iloveorlando.com/', icon: 'Ticket' },
    { id: 'greatworkperks', name: 'GreatWorkPerks', url: 'https://www.greatworkperks.com/Account/Login?ReturnUrl=%2Fcar-rental%3FrefId%3Dgwp', icon: 'Car' },
  ],
};
const perkIconMap = { Plane, Gift, Globe, Ship, Ticket, Car, ExternalLink };

const ResourcesTab = () => {
  const { isAdmin } = useAuth();
  const [resources, setResources] = useState(DEFAULT_RESOURCES);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ url: '', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try { const res = await api.get('/team-resources'); if (res.data && Object.keys(res.data).length > 0) setResources(res.data); } catch (e) { console.warn('[HRPage.TeamResources] fetch failed', e); } finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = { ...resources, teamDrive: { ...resources.teamDrive, url: editForm.url, description: editForm.description } };
      await api.post('/team-resources', updated);
      setResources(updated); setEditOpen(false); toast.success('Resources updated');
    } catch (err) { toast.error(getApiErrorMessage(err, 'Failed')); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4" data-testid="resources-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Internal tools, documentation, and perks</p>
        {isAdmin() && (
          <Button variant="outline" size="sm" onClick={() => { setEditForm({ url: resources.teamDrive.url, description: resources.teamDrive.description }); setEditOpen(true); }} data-testid="edit-resources-btn">
            <Settings className="h-3.5 w-3.5 mr-1.5" />Edit Resources
          </Button>
        )}
      </div>

      {/* Team Drive */}
      <Card>
        <CardHeader className="py-2 px-4"><CardTitle className="text-sm flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" />{resources.teamDrive.title}</CardTitle></CardHeader>
        <CardContent className="px-4 pb-3 space-y-2">
          <Button className="bg-primary hover:bg-primary/90" size="sm" onClick={() => window.open(resources.teamDrive.url, '_blank')} data-testid="open-team-drive-btn">
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Open Team Google Drive
          </Button>
          <p className="text-xs text-slate-500">{resources.teamDrive.description}</p>
        </CardContent>
      </Card>

      {/* Perks */}
      <Card>
        <CardHeader className="py-2 px-4"><CardTitle className="text-sm flex items-center gap-2"><Gift className="h-4 w-4 text-accent" />PTC Complimentary Access</CardTitle></CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="grid gap-1.5">
            {resources.perks.map(perk => {
              const PerkIcon = perkIconMap[perk.icon] || ExternalLink;
              return (
                <a key={perk.id} href={perk.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-lg border hover:border-primary hover:bg-primary/5 transition-all group" data-testid={`perk-link-${perk.id}`}>
                  <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-colors"><PerkIcon className="h-4 w-4" /></div>
                  <span className="flex-1 text-sm font-medium text-slate-700 group-hover:text-primary">{perk.name}</span>
                  <ExternalLink className="h-3.5 w-3.5 text-slate-400 group-hover:text-primary" />
                </a>
              );
            })}
          </div>
          <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border text-xs text-slate-500">
            If a perk requires a prestigous-marketing.com email, contact <a href="mailto:support@prestigioustravelclub.org" className="text-primary hover:underline font-medium">support@prestigioustravelclub.org</a>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Team Resources</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Team Drive URL</Label><Input value={editForm.url} onChange={e => setEditForm({ ...editForm, url: e.target.value })} /></div>
            <div><Label>Description</Label><Input value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// =================== MAIN HR PAGE ===================
const HRPage = () => {
  const { user: currentUser, isAdmin, isInternal, canManageUsers } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'team-members');

  // v3.20-1504 PARTNER SECURITY LOCKDOWN — computed at top of render so
  // the resulting `useEffect` runs on every render (Rules of Hooks).
  const _role = (currentUser?.role || '').toLowerCase();
  const isInternalRole = _role.startsWith('ptc_') || _role === 'manager_director';

  const ALL_TAB_ITEMS = useMemo(() => ([
    { value: 'team-members', label: 'Team Members', icon: Users, internalOnly: false },
    { value: 'onboarding', label: 'Onboarding', icon: ClipboardList, internalOnly: false },
    { value: 'documents', label: 'Documents', icon: FileText, internalOnly: false },
    { value: 'training', label: 'Training', icon: GraduationCap, internalOnly: false },
    { value: 'performance', label: 'Performance', icon: BarChart3, internalOnly: true },
    { value: 'payroll', label: 'Payroll', icon: DollarSign, internalOnly: true },
    { value: 'team-resources', label: 'Resources', icon: FolderOpen, internalOnly: true },
    { value: 'access-control', label: 'Access Control', icon: Lock, internalOnly: true },
    { value: 'activity-logs', label: 'Activity Logs', icon: Activity, internalOnly: true },
  ]), []);
  const tabItems = useMemo(
    () => ALL_TAB_ITEMS.filter(t => isInternalRole || !t.internalOnly),
    [ALL_TAB_ITEMS, isInternalRole],
  );
  const allowedTabValues = useMemo(
    () => new Set(tabItems.map(t => t.value)),
    [tabItems],
  );

  // Sync tab from URL param
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  // v3.20-1504 — snap to a permitted tab if the URL or state points at
  // an internal-only tab the caller isn't allowed to see.
  useEffect(() => {
    if (!allowedTabValues.has(activeTab) && activeTab !== 'team-members') {
      setActiveTab('team-members');
    }
  }, [activeTab, allowedTabValues]);
  const safeActiveTab = allowedTabValues.has(activeTab) ? activeTab : 'team-members';

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, companiesRes] = await Promise.all([api.get('/users'), api.get('/companies')]);
      setUsers(usersRes.data || []);
      setCompanies(companiesRes.data || []);
    } catch { toast.error('Failed to load HR data'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!canManageUsers()) {
    return (
      <div className="text-center py-12" data-testid="hr-access-denied">
        <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Denied</h2>
        <p className="text-slate-500">You don't have permission to access HR.</p>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;

  // v3.15-667: Show relevant users based on user type
  const isExternalUser = currentUser?.role === 'company_principal' || currentUser?.role === 'company_admin' || currentUser?.role === 'company_director' || currentUser?.role === 'manager_director' || currentUser?.role === 'company_manager';
  const visibleUsers = isExternalUser 
    ? users.filter(u => u.company_id === currentUser?.company_id)
    : users.filter(u => {
        const comp = companies.find(co => co.id === u.company_id);
        return comp?.company_type === 'internal';
      });
  const visibleCompanies = isExternalUser
    ? companies.filter(c => c.id === currentUser?.company_id)
    : companies.filter(c => c.company_type === 'internal');

  return (
    <div className="space-y-4" data-testid="hr-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-primary" data-testid="employee-management-title">Employee Management</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your company&apos;s employees, permissions and Hub access. <span className="text-slate-400">·  {visibleUsers.length} team members across {visibleCompanies.length} companies</span></p>
      </div>

      <Tabs value={safeActiveTab} onValueChange={(v) => { if (allowedTabValues.has(v)) setActiveTab(v); }}>
        <TabsList className="flex h-9 w-full overflow-x-auto">
          {tabItems.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="text-xs gap-1 flex-shrink-0" data-testid={`hr-tab-${t.value}`}>
              <t.icon className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">{t.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="team-members"><SectionErrorBoundary label="Team Members" testId="hr-tab-error-team-members"><TeamMembersTab users={users} companies={companies} currentUser={currentUser} isAdmin={isAdmin} onRefresh={fetchData} allUsers={users} /></SectionErrorBoundary></TabsContent>
        <TabsContent value="onboarding"><SectionErrorBoundary label="Onboarding" testId="hr-tab-error-onboarding"><OnboardingTab onRefresh={fetchData} /></SectionErrorBoundary></TabsContent>
        <TabsContent value="documents"><SectionErrorBoundary label="Documents" testId="hr-tab-error-documents"><DocumentsTab users={users} /></SectionErrorBoundary></TabsContent>
        <TabsContent value="training"><SectionErrorBoundary label="Training" testId="hr-tab-error-training"><TrainingTab users={users} /></SectionErrorBoundary></TabsContent>
        {isInternalRole && (
          <>
            <TabsContent value="performance"><SectionErrorBoundary label="Performance" testId="hr-tab-error-performance"><PerformanceTab /></SectionErrorBoundary></TabsContent>
            <TabsContent value="payroll"><SectionErrorBoundary label="Payroll" testId="hr-tab-error-payroll"><PayrollTab /></SectionErrorBoundary></TabsContent>
            <TabsContent value="team-resources"><SectionErrorBoundary label="Resources" testId="hr-tab-error-resources"><ResourcesTab /></SectionErrorBoundary></TabsContent>
            <TabsContent value="access-control"><SectionErrorBoundary label="Access Control" testId="hr-tab-error-access-control"><AccessControlTab users={users} /></SectionErrorBoundary></TabsContent>
            <TabsContent value="activity-logs"><SectionErrorBoundary label="Activity Logs" testId="hr-tab-error-activity-logs"><ActivityLogsTab /></SectionErrorBoundary></TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
};

export default HRPage;
